import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Play, Pause, SkipForward, SkipBack, Heart, Share2, Loader2, AlertTriangle,
  ZoomIn, Settings, Search, X, Sparkles, Plus, LogOut, Shuffle, Crown,
  Link2, Image, Video, Layers, Check, Zap, Gift, CreditCard, Database,
  FolderHeart, Shield, XCircle, ArrowLeft, CheckCircle, Menu, Maximize2,
  Minimize2, FolderPlus, MoreHorizontal, ChevronDown,
} from 'lucide-react';
import { MediaItem, AppSettings, MediaType, DriveAuthSession, AuthorizedMediaItem } from '../types';
import type { PlanTier } from '../types/plan';
import type { PlanResponse } from '../services/planService';
import { mediaCache } from '../services/mediaCache';
import { imageProcessingService } from '../services/imageProcessingService';
import { isVideo, isPhoto, getMediaType } from '../utils/mediaType';
import { Picture } from './Picture';
import { ProcessedImage } from '../services/imageProcessingService';
import {
  initGapiClient,
  signInToGoogle,
  signOut,
  listFolderMediaFilesDetailed,
  isDevAuthBypassEnabled,
  getDevAuthSession,
} from '../services/driveService';
import { analyzeImage, generateSearchTags } from '../services/geminiService';
import { storageService, STORES } from '../services/storageService';
import { metadataService } from '../services/metadataService';
import { searchCacheService } from '../services/searchCacheService';
import { subscriptionService } from '../services/subscriptionService';
import { usageService } from '../services/usageService';
import { planService } from '../services/planService';
import { paymentService } from '../services/paymentService';
import { libraryService, type UpsertResponse } from '../services/libraryService';
import { summarizeImportOutcome } from '../services/importSummary';
import { getDriveFileFolderImportEmptyMessage } from '../services/driveFileLimitations';
import { openDrivePicker, parseDriveLinksAndIds, resolveBulkImportIntent } from '../services/pickerService';
import {
  isDropboxConfigured,
  connectDropbox,
  disconnectDropbox,
  getDropboxSession,
  importDropboxFolder,
  syncDropbox,
} from '../services/dropboxService';
import { cloudBackupService, CloudBackupApiError } from '../services/cloudBackupService';
import { accountService, DELETE_CONFIRMATION } from '../services/accountService';
import {
  buildLocalContentKey,
  shouldShowSignOutExportModal,
  syncLocalFileToDriveAndLibrary,
} from '../services/localSyncService';
import { collectionsService, type CollectionMeta as CollectionModel } from '../services/collectionsService';
import { uploadthingService, type UploadThingStoredFile } from '../services/uploadthingService';
import { SubscriptionTier, TIER_PRICING, TIER_DISPLAY_NAME, getTierLimits } from '../constants/subscriptionLimits';
import { addSentryBreadcrumb, captureSentryException, setSentryAuthContext, clearSentryAuthContext } from '../sentry';
import { Toaster, toast } from 'sonner';


// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
//  DESIGN: "ETHEREAL GLASS"
//
//  A luminous, floating glass-morphism player with every App.tsx
//  feature re-skinned in the ethereal aesthetic.
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const ACCENT_PALETTE = ['#C4B5FD', '#FDA4AF', '#A5F3FC', '#FDE68A', '#6EE7B7', '#DDD6FE', '#FCA5A5', '#93C5FD'];
const accentFor = (index: number) => ACCENT_PALETTE[index % ACCENT_PALETTE.length];

// â”€â”€â”€ Shared glass CSS â”€â”€â”€
const GLASS_STYLE = `
  @keyframes float1 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(30px,-50px) scale(1.1)} 66%{transform:translate(-20px,20px) scale(.95)} }
  @keyframes float2 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(-40px,30px) scale(.9)} 66%{transform:translate(50px,-20px) scale(1.05)} }
  @keyframes float3 { 0%,100%{transform:translate(0,0) scale(1) rotate(0)} 50%{transform:translate(20px,-40px) scale(1.15) rotate(10deg)} }
  @keyframes slideInRight { from{opacity:0;transform:translateX(40px)} to{opacity:1;transform:translateX(0)} }
  @keyframes fadeScale { from{opacity:0;transform:scale(.95)} to{opacity:1;transform:scale(1)} }
  @keyframes pulseGlow { 0%,100%{box-shadow:0 0 20px rgba(196,181,253,.2)} 50%{box-shadow:0 0 40px rgba(196,181,253,.4)} }
  @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
  .glass-panel { background:rgba(255,255,255,.04); backdrop-filter:blur(40px) saturate(1.5); -webkit-backdrop-filter:blur(40px) saturate(1.5); border:1px solid rgba(255,255,255,.06); }
  .glass-modal { background:rgba(12,10,20,.92); backdrop-filter:blur(60px) saturate(1.8); -webkit-backdrop-filter:blur(60px) saturate(1.8); border:1px solid rgba(255,255,255,.08); }
  .glass-input { background:rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.1); color:white; }
  .glass-input:focus { border-color:rgba(196,181,253,.4); outline:none; box-shadow:0 0 0 2px rgba(196,181,253,.15); }
  .glass-input::placeholder { color:rgba(255,255,255,.25); }
  .accent-btn { background:linear-gradient(135deg,var(--accent),var(--accent-dim)); box-shadow:0 8px 32px var(--accent-shadow); }
  .range-ethereal { -webkit-appearance:none; width:100%; height:20px; background:transparent; cursor:pointer; }
  .range-ethereal::-webkit-slider-runnable-track { background:rgba(255,255,255,.08); border-radius:999px; height:4px; }
  .range-ethereal::-webkit-slider-thumb { -webkit-appearance:none; width:18px; height:18px; border-radius:50%; background:var(--accent); box-shadow:0 0 12px var(--accent-shadow); cursor:pointer; margin-top:-7px; }
  .range-ethereal::-moz-range-track { background:rgba(255,255,255,.08); border-radius:999px; height:4px; border:none; }
  .range-ethereal::-moz-range-thumb { width:18px; height:18px; border-radius:50%; background:var(--accent); box-shadow:0 0 12px var(--accent-shadow); cursor:pointer; border:none; }
  .range-ethereal::-ms-track { background:transparent; border-color:transparent; color:transparent; height:4px; }
  .range-ethereal::-ms-fill-lower, .range-ethereal::-ms-fill-upper { background:rgba(255,255,255,.08); border-radius:999px; }
  .range-ethereal::-ms-thumb { width:18px; height:18px; border-radius:50%; background:var(--accent); box-shadow:0 0 12px var(--accent-shadow); cursor:pointer; border:none; }
`;

const SERIF = "'Cormorant Garamond', Georgia, serif";
const SANS = "'Outfit', sans-serif";
const MAX_CLOUD_BACKUP_BYTES = 12 * 1024 * 1024;
const SESSION_FULLSCREEN_PREF_KEY = 'refract-session-fullscreen-pref';
const RESTORE_TOAST_SESSION_KEY = 'refract-session-restore-toast-shown';
const MOBILE_BREAKPOINT_QUERY = '(max-width: 767px)';

const supportsNativeFullscreen = (): boolean =>
  typeof document !== 'undefined' && Boolean(document.fullscreenEnabled);

const isIOSLikeDevice = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

const inferMimeTypeFromName = (name: string): string => {
  const extension = name.toLowerCase().split('.').pop() || '';
  const mimeMap: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif',
    bmp: 'image/bmp',
    tiff: 'image/tiff',
    tif: 'image/tiff',
    svg: 'image/svg+xml',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    webm: 'video/webm',
    mkv: 'video/x-matroska',
    m4v: 'video/x-m4v',
    '3gp': 'video/3gpp',
  };
  return mimeMap[extension] || 'application/octet-stream';
};

const mapAuthorizedItemToMediaItem = (item: AuthorizedMediaItem): MediaItem => {
  if (item.source === 'dropbox') {
    return {
      id: item.fileId,
      name: item.name,
      mimeType: item.mimeType,
      source: 'dropbox',
      dropboxFile: {
        id: item.fileId,
        name: item.name,
        mimeType: item.mimeType,
        directLink: item.externalLink || '',
        thumbnailLink: item.thumbnailLink,
      },
      collectionIds: item.collectionIds,
      contentHash: item.contentHash,
      sizeBytes: item.sizeBytes,
      originalSource: item.originalSource,
    };
  }

  if (item.source === 'local') {
    return {
      id: item.fileId,
      name: item.name,
      mimeType: item.mimeType,
      source: 'local',
      collectionIds: item.collectionIds,
      contentHash: item.contentHash,
      sizeBytes: item.sizeBytes,
      originalSource: item.originalSource,
      syncStatus: 'device-only',
    };
  }

  return {
    id: item.fileId,
    name: item.name,
    mimeType: item.mimeType,
    source: 'drive',
    driveFile: {
      id: item.fileId,
      name: item.name,
      mimeType: item.mimeType,
      thumbnailLink: item.thumbnailLink,
    },
    collectionIds: item.collectionIds,
    contentHash: item.contentHash,
    sizeBytes: item.sizeBytes,
    originalSource: item.originalSource,
  };
};

const mapUploadThingFileToMediaItem = (file: UploadThingStoredFile): MediaItem => ({
  id: `uploadthing-${file.key}`,
  name: file.name,
  mimeType: inferMimeTypeFromName(file.name),
  source: 'uploadthing',
  uploadThingFile: {
    key: file.key,
  },
  sizeBytes: file.size,
  originalSource: 'uploadthing',
  syncStatus: 'synced',
});


// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
//  ETHEREAL PLAYER (media renderer with zoom/pan)
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

interface EtherealPlayerProps {
  item?: MediaItem;
  accessToken: string | null;
  onVideoEnded?: () => void;
  isActive: boolean;
  onToggleUI: () => void;
  onMediaLoaded?: () => void;
}

export const EtherealPlayer: React.FC<EtherealPlayerProps> = ({
  item, accessToken, onVideoEnded, isActive, onToggleUI, onMediaLoaded,
}) => {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [processedImages, setProcessedImages] = useState<ProcessedImage[]>([]);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const lastTouchDistance = useRef<number | null>(null);
  const tapTimer = useRef<number | null>(null);
  const lastTapTime = useRef<number>(0);
  const imgRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // â”€â”€â”€ Media Loading (mirrors Player.tsx) â”€â”€â”€
  useEffect(() => {
    if (!item) return;
    let isMounted = true;
    setStatus('loading'); setProcessedImages([]); setMediaUrl(null);
    setScale(1); setPosition({ x: 0, y: 0 });

    const isPhotoItem = isPhoto(item);
    const isVideoItem = isVideo(item);
    const isHeic = isPhotoItem && (
      item.mimeType === 'image/heic' || item.mimeType === 'image/heif' ||
      item.mimeType === 'image/heic-sequence' || item.mimeType === 'image/heif-sequence' ||
      item.name.toLowerCase().endsWith('.heic') || item.name.toLowerCase().endsWith('.heif')
    );

    const abortController = new AbortController();

    if (isPhotoItem) {
      if (isHeic) {
        mediaCache.load(item, accessToken)
          .then(url => { if (isMounted && !abortController.signal.aborted) { setMediaUrl(url); setStatus('loaded'); } })
          .catch(err => console.warn('HEIC thumbnail load failed:', err));
        mediaCache.loadProcessedImages(item, accessToken, abortController.signal)
          .then(images => {
            if (isMounted && !abortController.signal.aborted && images.length > 0) {
              setProcessedImages(images);
              const jpeg = images.find(img => img.format === 'jpeg' && img.size === 1024) || images.find(img => img.format === 'jpeg') || images[0];
              if (jpeg) setMediaUrl(jpeg.url);
            }
          })
          .catch(err => { if (isMounted && !abortController.signal.aborted && err.message !== 'Processing cancelled') console.error('Processed images:', err); });
      } else {
        mediaCache.load(item, accessToken)
          .then(url => { if (isMounted) { setMediaUrl(url); setStatus('loaded'); } })
          .catch(err => { if (isMounted) { console.error('Load Error:', err); setStatus('error'); } });
      }
    } else if (isVideoItem) {
      mediaCache.load(item, accessToken)
        .then(url => { if (isMounted) { setMediaUrl(url); setStatus('loaded'); } })
        .catch(err => { if (isMounted) { console.error('Load Error:', err); setStatus('error'); } });
    }

    return () => { isMounted = false; abortController.abort(); mediaCache.cancelProcessing(item.id); };
  }, [item, accessToken]);

  useEffect(() => {
    if (isActive && status === 'loaded' && item && isVideo(item) && videoRef.current) {
      videoRef.current.currentTime = 0; videoRef.current.play().catch(() => { });
    }
  }, [isActive, status, item]);

  useEffect(() => {
    if (!item || status !== 'loaded' || !mediaUrl) return;
    if (onMediaLoaded) onMediaLoaded();
  }, [item?.id, status, mediaUrl, onMediaLoaded]);

  // â”€â”€â”€ Zoom helpers â”€â”€â”€
  const resetZoom = () => { setScale(1); setPosition({ x: 0, y: 0 }); };
  const zoomIn = () => { setScale(2.5); setPosition({ x: 0, y: 0 }); };
  const clampPosition = (x: number, y: number, cs: number) => {
    if (!containerRef.current) return { x, y };
    const { width, height } = containerRef.current.getBoundingClientRect();
    if (cs <= 1) return { x: 0, y: 0 };
    const mX = (width * cs - width) / 2; const mY = (height * cs - height) / 2;
    return { x: Math.max(-mX, Math.min(mX, x)), y: Math.max(-mY, Math.min(mY, y)) };
  };
  const getTouchDist = (t: React.TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (item && isVideo(item)) return;
    if (e.touches.length === 2) { lastTouchDistance.current = getTouchDist(e.touches); setIsDragging(false); }
    else if (e.touches.length === 1) { setIsDragging(scale > 1); dragStart.current = { x: e.touches[0].clientX - position.x, y: e.touches[0].clientY - position.y }; }
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (item && isVideo(item)) return;
    if (e.touches.length === 2 && lastTouchDistance.current !== null) {
      const nd = getTouchDist(e.touches); const d = nd - lastTouchDistance.current;
      const ns = Math.min(Math.max(1, scale + d * 0.005 * scale), 5);
      setScale(ns); lastTouchDistance.current = nd; if (ns <= 1) setPosition({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && scale > 1) {
      if (!isDragging) setIsDragging(true);
      setPosition(clampPosition(e.touches[0].clientX - dragStart.current.x, e.touches[0].clientY - dragStart.current.y, scale));
    }
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (item && isVideo(item)) { onToggleUI(); return; }
    const wasDrag = isDragging; lastTouchDistance.current = null; setIsDragging(false);
    if (wasDrag) return;
    if (e.changedTouches.length === 1) {
      const now = Date.now();
      if (now - lastTapTime.current < 300) { if (tapTimer.current) clearTimeout(tapTimer.current); tapTimer.current = null; if (scale > 1) resetZoom(); else zoomIn(); lastTapTime.current = 0; }
      else { lastTapTime.current = now; tapTimer.current = window.setTimeout(() => { if (scale === 1) onToggleUI(); tapTimer.current = null; }, 300); }
    }
  };
  const handleMouseDown = (e: React.MouseEvent) => { if (scale > 1 && e.button === 0) { setIsDragging(true); dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y }; } };
  const handleMouseMove = (e: React.MouseEvent) => { if (isDragging && scale > 1) setPosition(clampPosition(e.clientX - dragStart.current.x, e.clientY - dragStart.current.y, scale)); };
  const handleMouseUp = () => { setIsDragging(false); };
  const handleMouseClick = (e: React.MouseEvent) => {
    if (isDragging) return;
    const now = Date.now();
    if (now - lastTapTime.current < 300) { if (tapTimer.current) clearTimeout(tapTimer.current); if (scale > 1) resetZoom(); else zoomIn(); lastTapTime.current = 0; }
    else { lastTapTime.current = now; tapTimer.current = window.setTimeout(() => { if (scale === 1) onToggleUI(); }, 300); }
  };
  const handleWheel = (e: React.WheelEvent) => { if (item && isVideo(item)) return; const ns = Math.min(Math.max(1, scale + (-e.deltaY * 0.001)), 5); setScale(ns); if (ns === 1) setPosition({ x: 0, y: 0 }); };

  if (!item) return <div className="w-full h-full bg-transparent" />;
  if (status === 'error') return (
    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 rounded-3xl">
      <AlertTriangle size={48} className="mb-4 text-rose-400" />
      <p className="text-lg font-bold" style={{ fontFamily: SERIF }}>Failed to load media</p>
      <p className="text-xs mt-2 text-white/30">{item.name}</p>
    </div>
  );
  const isVid = isVideo(item);

  return (
    <div ref={containerRef} className="relative w-full h-full flex items-center justify-center overflow-hidden touch-none"
      onWheel={handleWheel} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onClick={handleMouseClick}
    >
      {status === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
          <Loader2 className="text-white/40 animate-spin" size={40} />
        </div>
      )}
      {mediaUrl && (
        <div className="relative w-full h-full flex items-center justify-center will-change-transform"
          style={{ transform: `translate(${position.x}px,${position.y}px) scale(${scale})`, transition: isDragging || lastTouchDistance.current ? 'none' : 'transform 0.2s ease-out', cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}>
          {isVid ? (
            <video ref={videoRef} src={mediaUrl} className="max-w-full max-h-full object-contain rounded-2xl" controls playsInline
              onEnded={onVideoEnded} onLoadedData={() => onMediaLoaded?.()} onError={() => onMediaLoaded?.()} style={{ pointerEvents: 'auto' }} onClick={e => e.stopPropagation()} />
          ) : processedImages.length > 0 ? (
            <div className="max-w-full max-h-full"><Picture images={processedImages} alt={item.name} className="max-w-full max-h-full object-contain select-none pointer-events-auto rounded-2xl" loading="eager" onLoad={() => onMediaLoaded?.()} onError={() => onMediaLoaded?.()} sizes="100vw" /></div>
          ) : mediaUrl ? (
            <img ref={imgRef} src={mediaUrl} alt={item.name} className="max-w-full max-h-full object-contain select-none pointer-events-auto rounded-2xl" draggable={false} onLoad={() => onMediaLoaded?.()} onError={() => onMediaLoaded?.()} />
          ) : null}
        </div>
      )}
      {!isVid && status === 'loaded' && scale === 1 && isActive && (
        <div className="absolute top-4 right-4 text-white/15 pointer-events-none animate-pulse"><ZoomIn size={20} /></div>
      )}
    </div>
  );
};


// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
//  GLASS MODALS - all App.tsx modals re-skinned
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

// â”€â”€â”€ SETTINGS MODAL â”€â”€â”€
const GlassSettingsModal: React.FC<{
  settings: AppSettings;
  cloudBackupEnabled: boolean;
  isCloudBackupAvailable: boolean;
  onToggleCloudBackup: (enabled: boolean) => void;
  dropboxConnected: boolean;
  onConnectDropbox: () => void;
  onSyncDropbox: () => void;
  onDisconnectDropbox: () => void;
  onClose: () => void;
  onUpdate: (s: Partial<AppSettings>) => void;
  onDeleteAccount: () => Promise<void>;
  accent: string;
}> = ({
  settings,
  cloudBackupEnabled,
  isCloudBackupAvailable,
  onToggleCloudBackup,
  dropboxConnected,
  onConnectDropbox,
  onSyncDropbox,
  onDisconnectDropbox,
  onClose,
  onUpdate,
  onDeleteAccount,
  accent,
}) => {
  // Use local state to track changes - only apply on "Done"
  const [localFilter, setLocalFilter] = useState(settings.filter);
  const [localInterval, setLocalInterval] = useState(settings.interval);
  const [dangerOpen, setDangerOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDone = () => {
    // Only update if something actually changed
    const changes: Partial<AppSettings> = {};
    if (localFilter !== settings.filter) changes.filter = localFilter;
    if (localInterval !== settings.interval) changes.interval = localInterval;
    if (Object.keys(changes).length > 0) {
      onUpdate(changes);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-4 pt-6 sm:pt-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />
      <div className="relative glass-modal rounded-3xl w-full max-w-md p-6 sm:p-8 shadow-2xl max-h-[90dvh] overflow-y-auto" onClick={e => e.stopPropagation()}
        style={{ '--accent': accent, '--accent-dim': accent + '80', '--accent-shadow': accent + '40' } as React.CSSProperties}>
        <button onClick={onClose} className="absolute top-5 right-5 text-white/30 hover:text-white transition-colors"><X size={20} /></button>
        <h2 className="text-3xl font-light text-white mb-8" style={{ fontFamily: SERIF }}>Playback Settings</h2>

        <div className="mb-8">
          <label className="text-white/30 text-[10px] tracking-[.25em] uppercase block mb-3" style={{ fontFamily: SANS, fontWeight: 300 }}>Media Filter</label>
          <div className="grid grid-cols-3 gap-3">
            {[{ type: MediaType.ALL, icon: Layers, label: 'All' }, { type: MediaType.PHOTO, icon: Image, label: 'Photos' }, { type: MediaType.VIDEO, icon: Video, label: 'Videos' }].map(item => (
              <button key={item.type} onClick={() => setLocalFilter(item.type)}
                className={`flex flex-col items-center justify-center p-4 rounded-2xl transition-all duration-300 ${localFilter === item.type ? 'text-white' : 'glass-panel text-white/30 hover:text-white/60'}`}
                style={localFilter === item.type ? { background: `linear-gradient(135deg, ${accent}40, ${accent}15)`, border: `1px solid ${accent}40` } : {}}>
                <item.icon size={22} className="mb-2" />
                <span className="text-xs font-medium" style={{ fontFamily: SANS }}>{item.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Media Duration */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-3">
            <label className="text-white/30 text-[10px] tracking-[.25em] uppercase" style={{ fontFamily: SANS, fontWeight: 300 }}>Photo Duration</label>
            <span className="text-lg font-light" style={{ fontFamily: SERIF, color: accent }}>{localInterval}s</span>
          </div>

          {/* Media Duration Slider */}
          <div className="py-2">
            <input type="range" min="0.5" max="30" step="0.5" value={localInterval}
              onChange={e => setLocalInterval(parseFloat(e.target.value))}
              className="range-ethereal w-full" style={{ '--accent': accent, '--accent-shadow': accent + '40' } as React.CSSProperties} />
          </div>
          <div className="flex justify-between text-[10px] text-white/15 mt-2" style={{ fontFamily: SANS }}><span>0.5s</span><span>30s</span></div>
        </div>

        <div className="mb-8 rounded-2xl glass-panel p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <label className="text-white/30 text-[10px] tracking-[.25em] uppercase block mb-1" style={{ fontFamily: SANS, fontWeight: 300 }}>
                Cloud Backup
              </label>
              <p className="text-white/25 text-xs leading-relaxed" style={{ fontFamily: SANS, fontWeight: 300 }}>
                Files stay local in your browser by default. Optional cloud backup adds cross-device restore.
              </p>
            </div>
            <button
              type="button"
              onClick={() => onToggleCloudBackup(!cloudBackupEnabled)}
              disabled={!isCloudBackupAvailable && !cloudBackupEnabled}
              className={`px-3 py-2 rounded-xl text-xs transition-colors ${
                cloudBackupEnabled
                  ? 'bg-emerald-500/20 text-emerald-300'
                  : 'bg-white/5 text-white/40 hover:text-white/70'
              } ${!isCloudBackupAvailable && !cloudBackupEnabled ? 'opacity-60 cursor-not-allowed' : ''}`}
              style={{ fontFamily: SANS, fontWeight: 500 }}
            >
              {cloudBackupEnabled ? 'Enabled' : isCloudBackupAvailable ? 'Enable' : 'Pro Feature'}
            </button>
          </div>
        </div>

        <div className="mb-8 rounded-2xl glass-panel p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <label className="text-white/30 text-[10px] tracking-[.25em] uppercase block mb-1" style={{ fontFamily: SANS, fontWeight: 300 }}>
                Dropbox
              </label>
              <p className="text-white/25 text-xs leading-relaxed" style={{ fontFamily: SANS, fontWeight: 300 }}>
                {dropboxConnected
                  ? 'Connected. Import recursively and run incremental sync with Dropbox cursors.'
                  : 'Connect Dropbox to import folders recursively and keep them synced.'}
              </p>
            </div>
            <div className="flex gap-2">
              {dropboxConnected ? (
                <>
                  <button
                    type="button"
                    onClick={onSyncDropbox}
                    className="px-3 py-2 rounded-xl text-xs bg-blue-500/20 text-blue-300 hover:bg-blue-500/30"
                    style={{ fontFamily: SANS, fontWeight: 500 }}
                  >
                    Sync
                  </button>
                  <button
                    type="button"
                    onClick={onDisconnectDropbox}
                    className="px-3 py-2 rounded-xl text-xs bg-rose-500/20 text-rose-300 hover:bg-rose-500/30"
                    style={{ fontFamily: SANS, fontWeight: 500 }}
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={onConnectDropbox}
                  className="px-3 py-2 rounded-xl text-xs bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30"
                  style={{ fontFamily: SANS, fontWeight: 500 }}
                >
                  Connect
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Danger Zone — collapsed by default so an irreversible action is never one stray tap away */}
        <div className="mb-8 rounded-2xl overflow-hidden border border-rose-500/15 bg-rose-500/[0.03]">
          <button
            type="button"
            onClick={() => { setDangerOpen(o => !o); setDeleteConfirmText(''); }}
            aria-expanded={dangerOpen}
            className="w-full flex items-center justify-between gap-3 p-4 text-left transition-colors hover:bg-rose-500/[0.06]"
          >
            <span className="text-rose-300/50 text-[10px] tracking-[.25em] uppercase" style={{ fontFamily: SANS, fontWeight: 300 }}>
              Danger Zone
            </span>
            <ChevronDown
              size={16}
              className="text-rose-300/40 transition-transform duration-300"
              style={{ transform: dangerOpen ? 'rotate(180deg)' : 'none' }}
            />
          </button>

          {dangerOpen && (
            <div className="px-4 pb-4" style={{ animation: 'fadeScale 0.25s ease-out' }}>
              <p className="text-white/30 text-xs leading-relaxed mb-1" style={{ fontFamily: SANS, fontWeight: 300 }}>
                Deleting your account permanently erases your library index, collections, settings,
                cloud backup files, and connected accounts. Any active subscription is cancelled.
              </p>
              <p className="text-rose-300/50 text-xs leading-relaxed mb-4" style={{ fontFamily: SANS, fontWeight: 300 }}>
                Photos in your Google Drive and Dropbox are not touched. This cannot be undone.
              </p>

              <label
                htmlFor="delete-confirm"
                className="text-white/25 text-[10px] tracking-[.2em] uppercase block mb-2"
                style={{ fontFamily: SANS, fontWeight: 300 }}
              >
                Type <span className="text-rose-300/70">{DELETE_CONFIRMATION}</span> to confirm
              </label>
              <input
                id="delete-confirm"
                type="text"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                disabled={isDeleting}
                autoComplete="off"
                spellCheck={false}
                placeholder={DELETE_CONFIRMATION}
                className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/15 focus:outline-none focus:border-rose-400/40 transition-colors mb-3"
                style={{ fontFamily: SANS, fontWeight: 300 }}
              />

              <button
                type="button"
                onClick={async () => {
                  setIsDeleting(true);
                  try { await onDeleteAccount(); } finally { setIsDeleting(false); }
                }}
                disabled={deleteConfirmText !== DELETE_CONFIRMATION || isDeleting}
                className="w-full py-3 rounded-xl text-sm flex items-center justify-center gap-2 transition-all duration-300 bg-rose-500/15 text-rose-200 border border-rose-500/30 enabled:hover:bg-rose-500/25 enabled:active:scale-[0.98] disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ fontFamily: SANS, fontWeight: 500 }}
              >
                {isDeleting && <Loader2 size={15} className="animate-spin" />}
                {isDeleting ? 'Deleting your account...' : 'Delete my account'}
              </button>
            </div>
          )}
        </div>

        {/* Done Button */}
        <button onClick={handleDone} className="w-full py-3 rounded-2xl text-sm font-medium transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
          style={{ background: `linear-gradient(135deg, ${accent}, ${accent}80)`, color: '#0C0A14', fontFamily: SANS }}>
          Done
        </button>
      </div>
    </div>
  );
};

// â”€â”€â”€ SEARCH MODAL â”€â”€â”€
const GlassSearchModal: React.FC<{
  isOpen: boolean; onClose: () => void; onSearch: (q: string) => void; isProcessing: boolean; accent: string;
}> = ({ isOpen, onClose, onSearch, isProcessing, accent }) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (isOpen) setTimeout(() => inputRef.current?.focus(), 100); }, [isOpen]);
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); if (query.trim()) onSearch(query); };
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center pt-20 px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-xl" onClick={onClose} />
      <div className="relative w-full max-w-xl glass-modal rounded-3xl shadow-2xl overflow-hidden" style={{ animation: 'fadeScale 0.3s ease-out' }}>
        <form onSubmit={handleSubmit}>
          <div className="flex items-center px-6 py-5 gap-4">
            {isProcessing ? <Loader2 className="animate-spin" size={22} style={{ color: accent }} /> : <Search className="text-white/30" size={22} />}
            <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search your photos (e.g., 'beach vacation')"
              className="flex-1 bg-transparent text-white text-lg placeholder-white/20 focus:outline-none" style={{ fontFamily: SANS, fontWeight: 300 }}
              disabled={isProcessing} />
            {query && !isProcessing && <button type="button" onClick={() => setQuery('')} className="text-white/20 hover:text-white/50"><X size={18} /></button>}
          </div>
          <div className="border-t border-white/5 px-6 py-3 flex items-center justify-between" style={{ background: `linear-gradient(90deg, ${accent}08, transparent)` }}>
            <div className="flex items-center gap-2 text-[11px] font-medium" style={{ color: accent + '90', fontFamily: SANS }}>
              <Sparkles size={12} /><span>AI-Powered Visual Search</span>
            </div>
            <button type="button" onClick={onClose} className="text-[11px] text-white/20 hover:text-white/50" style={{ fontFamily: SANS }}>ESC</button>
          </div>
        </form>
        {isProcessing && (
          <div className="px-6 py-3 text-sm text-white/30" style={{ fontFamily: SANS, fontWeight: 200 }}>
            Expanding query with Gemini and scanning Drive...
          </div>
        )}
      </div>
    </div>
  );
};

// â”€â”€â”€ PASTE LINKS MODAL â”€â”€â”€
const GlassPasteLinksModal: React.FC<{
  isOpen: boolean; initialValue?: string; onClose: () => void; onSubmit: (v: string) => void; accent: string;
}> = ({ isOpen, initialValue = '', onClose, onSubmit, accent }) => {
  const [value, setValue] = useState(initialValue);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { if (isOpen) { setValue(initialValue); setTimeout(() => ref.current?.focus(), 100); } }, [isOpen, initialValue]);
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-xl" onClick={onClose} />
      <div className="relative w-full max-w-2xl glass-modal rounded-3xl shadow-2xl max-h-[90dvh] overflow-y-auto" style={{ animation: 'fadeScale 0.3s ease-out' }}>
        <div className="p-6 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3"><Link2 size={18} style={{ color: accent }} /><h2 className="text-xl font-light text-white" style={{ fontFamily: SERIF }}>Paste Drive Links</h2></div>
          <button onClick={onClose} className="text-white/30 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-white/30" style={{ fontFamily: SANS, fontWeight: 200 }}>Paste one Drive file/folder link per line. File links are preselected; folder-only input opens Picker so you can select the folder and import media.</p>
          <textarea ref={ref} value={value} onChange={e => setValue(e.target.value)} rows={8}
            placeholder={`https://drive.google.com/file/d/<ID>/view\nhttps://drive.google.com/drive/folders/<ID>\n<ID>`}
            className="w-full rounded-2xl glass-input p-4 text-sm focus:ring-0 resize-none" style={{ fontFamily: SANS }} />
        </div>
        <div className="px-6 py-4 border-t border-white/5 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl glass-panel text-white/50 hover:text-white text-sm transition-colors" style={{ fontFamily: SANS }}>Cancel</button>
          <button onClick={() => onSubmit(value)} className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all hover:scale-[1.02] active:scale-[.98]"
            style={{ background: `linear-gradient(135deg, ${accent}, ${accent}80)`, color: '#0C0A14', fontFamily: SANS }}>
            Continue in Picker
          </button>
        </div>
      </div>
    </div>
  );
};

const normalizeDropboxPath = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '/';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

const GlassDropboxImportModal: React.FC<{
  isOpen: boolean;
  initialPath: string;
  initialRecursive: boolean;
  loading: boolean;
  error: string | null;
  onCancel: () => void;
  onImport: (path: string, recursive: boolean) => Promise<void>;
}> = ({
  isOpen,
  initialPath,
  initialRecursive,
  loading,
  error,
  onCancel,
  onImport,
}) => {
  const [path, setPath] = useState(initialPath);
  const [recursive, setRecursive] = useState(initialRecursive);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setPath(initialPath);
    setRecursive(initialRecursive);
    window.setTimeout(() => inputRef.current?.focus(), 60);
  }, [isOpen, initialPath, initialRecursive]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[82] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/65 backdrop-blur-md" onClick={loading ? undefined : onCancel} />
      <form
        className="relative w-full max-w-lg glass-modal rounded-3xl p-6"
        onSubmit={async (event) => {
          event.preventDefault();
          await onImport(path, recursive);
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl text-white font-light" style={{ fontFamily: SERIF }}>Import Dropbox folder</h3>
          <button type="button" onClick={onCancel} disabled={loading} className="text-white/25 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-white/45 mb-4" style={{ fontFamily: SANS }}>
          Choose the Dropbox folder path to index. Leave blank to import from root.
        </p>
        <label className="text-[11px] tracking-[.08em] uppercase text-white/30 block mb-2" style={{ fontFamily: SANS }}>
          Folder path
        </label>
        <input
          ref={inputRef}
          value={path}
          onChange={(event) => setPath(event.target.value)}
          placeholder="/"
          className="w-full glass-input rounded-xl px-3 py-2.5 text-sm"
          style={{ fontFamily: SANS }}
        />
        <div className="mt-4 flex items-center gap-2 text-sm text-white/55" style={{ fontFamily: SANS }}>
          <input
            id="dropbox-recursive"
            type="checkbox"
            checked={recursive}
            onChange={(event) => setRecursive(event.target.checked)}
            className="h-4 w-4 rounded border-white/20 bg-white/5"
          />
          <label htmlFor="dropbox-recursive">Import recursively</label>
        </div>
        <p className="mt-3 text-xs text-white/35" style={{ fontFamily: SANS }}>
          Normalized path: <span className="text-white/60">{normalizeDropboxPath(path)}</span>
        </p>
        {error && (
          <div className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300/90" style={{ fontFamily: SANS }}>
            {error}
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={loading} className="px-4 py-2.5 rounded-xl glass-panel text-white/50 hover:text-white text-sm" style={{ fontFamily: SANS }}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2.5 rounded-xl text-sm font-medium transition-all hover:scale-[1.02] active:scale-[.98] flex items-center gap-2"
            style={{ background: 'linear-gradient(135deg, #818CF8, #A5B4FC)', color: '#0C0A14', fontFamily: SANS }}
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : null}
            {loading ? 'Importing...' : 'Import'}
          </button>
        </div>
      </form>
    </div>
  );
};

// â”€â”€â”€ SUBSCRIPTION MODAL â”€â”€â”€
const GlassSubscriptionModal: React.FC<{
  isOpen: boolean; onClose: () => void; userId: string; session?: DriveAuthSession | null;
  onPlanChange?: (plan: PlanResponse) => void; accent: string; indexedItemsUsed?: number | null; usageLoading?: boolean;
}> = ({ isOpen, onClose, userId, session, onPlanChange, accent, indexedItemsUsed, usageLoading = false }) => {
  const [plan, setPlan] = useState<PlanResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [targetTier, setTargetTier] = useState<PlanTier>('pro');
  const [loadingPortal, setLoadingPortal] = useState(false);

  useEffect(() => { if (isOpen && session) loadData(); }, [isOpen, session]);
  const loadData = async () => {
    if (!session) return;
    setLoading(true);
    try {
      const d = await planService.getPlan(session);
      setPlan(d);
      onPlanChange?.(d);
    } catch {
      // keep previous plan snapshot on transient failures
    } finally {
      setLoading(false);
    }
  };
  const handleUpgrade = (tier: PlanTier) => { setTargetTier(tier); setShowUpgrade(true); };
  const handleManageBilling = async () => { setLoadingPortal(true); try { const url = await paymentService.getCustomerPortalUrl(userId); window.location.href = url; } catch { alert('Failed to open billing portal.'); setLoadingPortal(false); } };

  if (!isOpen) return null;
  if (loading) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xl"><Loader2 className="w-8 h-8 text-white/40 animate-spin" /></div>
  );

  const currentTier = plan?.tier || 'free';
  const fmtNum = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(n));
  const tiers: { key: PlanTier; icon: React.ReactNode; gradient: string }[] = [
    { key: 'free', icon: <Gift className="w-5 h-5 text-white/40" />, gradient: 'from-white/5 to-white/2' },
    { key: 'pro', icon: <Crown className="w-5 h-5 text-yellow-400" />, gradient: 'from-yellow-500/10 to-yellow-500/3' },
    { key: 'power', icon: <Zap className="w-5 h-5 text-purple-400" />, gradient: 'from-purple-500/10 to-purple-500/3' },
  ];

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-xl" onClick={onClose} />
        <div className="relative glass-modal rounded-3xl p-8 max-w-3xl w-full shadow-2xl max-h-[90vh] overflow-y-auto" style={{ animation: 'fadeScale 0.3s ease-out' }}>
          <div className="flex justify-between items-start mb-8">
            <div>
              <h2 className="text-3xl font-light text-white" style={{ fontFamily: SERIF }}>Plans & Usage</h2>
              {plan?.usage && (
                <p className="text-sm text-white/25 mt-2" style={{ fontFamily: SANS, fontWeight: 200 }}>
                  <Database size={12} className="inline mr-1" />
                  {usageLoading ? '—' : fmtNum(indexedItemsUsed ?? plan.usage.driveItemsUsed)} / {fmtNum(plan.usage.driveItemsLimit)} Indexed items
                  {plan.features.aiSearch && <> · <Sparkles size={12} className="inline mr-1 text-emerald-400/70" />AI</>}
                  {plan.features.collections && <> · <FolderHeart size={12} className="inline mr-1 text-blue-400/70" />Collections</>}
                </p>
              )}
            </div>
            <button onClick={onClose} className="text-white/20 hover:text-white transition-colors"><X size={20} /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {tiers.map(({ key, icon, gradient }) => {
              const limits = getTierLimits(key);
              const isCurrent = currentTier === key;
              const tierAccent = key === 'pro' ? '#FBBF24' : key === 'power' ? '#A78BFA' : 'rgba(255,255,255,.3)';
              return (
                <div key={key} className={`p-5 rounded-2xl bg-gradient-to-b ${gradient} transition-all duration-300`}
                  style={{ border: isCurrent ? `2px solid ${tierAccent}50` : '1px solid rgba(255,255,255,.05)' }}>
                  <div className="flex items-center gap-2 mb-3">{icon}<h4 className="font-medium text-white text-sm" style={{ fontFamily: SANS }}>{TIER_DISPLAY_NAME[key]}</h4></div>
                  <div className="text-2xl font-light text-white mb-3" style={{ fontFamily: SERIF }}>
                    {TIER_PRICING[key].monthly === 0 ? 'Free' : `$${TIER_PRICING[key].monthly}`}
                    {TIER_PRICING[key].monthly > 0 && <span className="text-xs font-normal text-white/25" style={{ fontFamily: SANS }}>/mo</span>}
                  </div>
                  <ul className="space-y-1.5 text-xs text-white/40 mb-4" style={{ fontFamily: SANS, fontWeight: 200 }}>
                    <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400/70" />{fmtNum(limits.driveItemsLimit)} Drive items</li>
                    <li className="flex items-center gap-1.5">{limits.aiSearchTags ? <Check className="w-3 h-3 text-emerald-400/70" /> : <X className="w-3 h-3 text-white/15" />}AI search</li>
                    <li className="flex items-center gap-1.5">{limits.collectionsEnabled ? <Check className="w-3 h-3 text-emerald-400/70" /> : <X className="w-3 h-3 text-white/15" />}Collections</li>
                    <li className="flex items-center gap-1.5">{limits.cloudBackup ? <Check className="w-3 h-3 text-emerald-400/70" /> : <X className="w-3 h-3 text-white/15" />}Cloud backup</li>
                  </ul>
                  {isCurrent ? (
                    <div className="text-[10px] text-center py-2 rounded-xl" style={{ color: tierAccent, fontFamily: SANS, fontWeight: 500 }}>Current Plan</div>
                  ) : key !== 'free' ? (
                    <button onClick={() => handleUpgrade(key)} className="w-full py-2.5 rounded-xl text-xs font-medium transition-all hover:scale-[1.02] active:scale-[.98]"
                      style={{ background: `linear-gradient(135deg, ${tierAccent}, ${tierAccent}80)`, color: '#0C0A14', fontFamily: SANS }}>
                      Upgrade to {TIER_DISPLAY_NAME[key]}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>

          {currentTier !== 'free' && (
            <button onClick={handleManageBilling} disabled={loadingPortal}
              className="w-full glass-panel rounded-2xl py-3 text-white/50 hover:text-white text-sm font-medium transition-all flex items-center justify-center gap-2 mb-4"
              style={{ fontFamily: SANS }}>
              {loadingPortal ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
              {loadingPortal ? 'Opening...' : 'Manage Billing'}
            </button>
          )}

          <div className="flex justify-end">
            <button onClick={onClose} className="text-white/20 hover:text-white text-sm transition-colors" style={{ fontFamily: SANS }}>Close</button>
          </div>
        </div>
      </div>

      {showUpgrade && (
        <GlassUpgradePrompt isOpen={showUpgrade} onClose={() => setShowUpgrade(false)}
          reason={`Upgrade to ${TIER_DISPLAY_NAME[targetTier]} for more storage and features`}
          currentTier={currentTier} targetTier={targetTier} userId={userId} accent={accent} />
      )}
    </>
  );
};

// â”€â”€â”€ UPGRADE PROMPT â”€â”€â”€
const GlassUpgradePrompt: React.FC<{
  isOpen: boolean; onClose: () => void; reason: string; currentTier: SubscriptionTier; targetTier: SubscriptionTier;
  userId: string | null; onUpgrade?: () => void; accent: string;
}> = ({ isOpen, onClose, reason, currentTier, targetTier, userId, onUpgrade, accent }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'monthly' | 'yearly'>('monthly');
  if (!isOpen) return null;

  const pricing = TIER_PRICING[targetTier];
  const tierName = TIER_DISPLAY_NAME[targetTier];
  const limits = getTierLimits(targetTier);
  const fmtNum = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(n));
  const benefits: string[] = [];
  benefits.push(`Up to ${fmtNum(limits.driveItemsLimit)} Drive items`);
  if (limits.aiSearchTags) benefits.push('AI-powered search tags');
  if (limits.collectionsEnabled) benefits.push(`Collections (up to ${limits.collectionsLimit === -1 ? 'unlimited' : limits.collectionsLimit})`);
  if (limits.premiumFeatures.length > 0) benefits.push('Smart Shuffle, Memory Recaps, Priority Processing');

  const handleUpgrade = async () => {
    if (onUpgrade) { onUpgrade(); return; }
    if (!userId) {
      setError('Please sign in to upgrade your plan.');
      return;
    }
    setLoading(true); setError(null);
    try { const url = await subscriptionService.getUpgradeUrl(userId, targetTier as PlanTier, selectedPeriod); window.location.href = url; }
    catch (err: any) { setError(err.message || 'Failed to start checkout.'); setLoading(false); }
  };

  const tierAccent = targetTier === 'pro' ? '#FBBF24' : '#A78BFA';
  return (
    <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-4 pt-6 sm:pt-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-xl" onClick={onClose} />
      <div className="relative glass-modal rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl max-h-[90dvh] overflow-y-auto" style={{ animation: 'fadeScale 0.3s ease-out' }}>
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-3"><Sparkles className="w-6 h-6" style={{ color: tierAccent }} /><h2 className="text-2xl font-light text-white" style={{ fontFamily: SERIF }}>Upgrade to {tierName}</h2></div>
          <button onClick={onClose} className="text-white/20 hover:text-white"><X size={18} /></button>
        </div>
        <p className="text-white/30 text-sm mb-5" style={{ fontFamily: SANS, fontWeight: 200 }}>{reason}</p>
        <ul className="space-y-2.5 mb-6">
          {benefits.map((b, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-white/40" style={{ fontFamily: SANS, fontWeight: 300 }}>
              <Zap className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: tierAccent }} /><span>{b}</span>
            </li>
          ))}
        </ul>
        <div className="mb-5 p-5 rounded-2xl glass-panel">
          <div className="flex items-baseline gap-2 mb-4">
            <span className="text-3xl font-light text-white" style={{ fontFamily: SERIF }}>${selectedPeriod === 'monthly' ? pricing.monthly : pricing.yearly}</span>
            <span className="text-white/20 text-sm" style={{ fontFamily: SANS }}>/{selectedPeriod === 'monthly' ? 'month' : 'year'}</span>
          </div>
          <div className="flex gap-2">
            {(['monthly', 'yearly'] as const).map(period => (
              <button key={period} onClick={() => setSelectedPeriod(period)}
                className={`flex-1 px-3 py-2.5 rounded-xl text-xs font-medium transition-all ${selectedPeriod === period ? 'text-white' : 'glass-panel text-white/30 hover:text-white/50'}`}
                style={selectedPeriod === period ? { background: `linear-gradient(135deg, ${tierAccent}60, ${tierAccent}30)`, border: `1px solid ${tierAccent}40` } : { fontFamily: SANS }}>
                {period === 'monthly' ? 'Monthly' : <>Yearly <span className="text-emerald-400/70 text-[10px]">(Save 17%)</span></>}
              </button>
            ))}
          </div>
        </div>
        {error && <div className="mb-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm" style={{ fontFamily: SANS }}>{error}</div>}
        <div className="flex gap-3">
          <button onClick={handleUpgrade} disabled={loading}
            className="flex-1 py-3 rounded-2xl text-sm font-medium transition-all hover:scale-[1.02] active:scale-[.98] flex items-center justify-center gap-2"
            style={{ background: `linear-gradient(135deg, ${tierAccent}, ${tierAccent}80)`, color: '#0C0A14', fontFamily: SANS }}>
            {loading ? <><Loader2 size={16} className="animate-spin" />Processing...</> : <><Shield size={16} />Upgrade to {tierName}</>}
          </button>
          <button onClick={onClose} disabled={loading} className="px-4 py-3 rounded-2xl text-white/20 hover:text-white/50 glass-panel text-sm transition-colors" style={{ fontFamily: SANS }}>Later</button>
        </div>
      </div>
    </div>
  );
};

// â”€â”€â”€ AI ANALYSIS OVERLAY â”€â”€â”€
const GlassAIOverlay: React.FC<{
  analysis: string | null; isAnalyzing: boolean; onClose: () => void; accent: string;
}> = ({ analysis, isAnalyzing, onClose, accent }) => {
  if (!analysis && !isAnalyzing) return null;
  return (
    <div className="absolute top-20 right-4 left-4 md:left-auto md:w-96 z-40 glass-modal rounded-2xl p-5 shadow-2xl" style={{ animation: 'fadeScale 0.3s ease-out' }}>
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-medium flex items-center gap-2 text-sm" style={{ fontFamily: SANS, color: accent }}>
          <Sparkles size={14} /> AI Analysis
        </h3>
        <button onClick={onClose} className="text-white/20 hover:text-white"><X size={14} /></button>
      </div>
      <div className="text-sm text-white/50 max-h-60 overflow-y-auto" style={{ fontFamily: SANS, fontWeight: 200 }}>
        {isAnalyzing ? (
          <div className="flex items-center gap-3 py-6 justify-center">
            {[0, 1, 2].map(i => <div key={i} className="w-2 h-2 rounded-full animate-bounce" style={{ background: accent, animationDelay: `${i * 75}ms` }} />)}
          </div>
        ) : <p className="leading-relaxed whitespace-pre-wrap">{analysis}</p>}
      </div>
    </div>
  );
};

// â”€â”€â”€ LIBRARY USAGE BAR (ethereal) â”€â”€â”€
const GlassUsageBar: React.FC<{
  used: number; limit: number; tier: PlanTier; aiEnabled: boolean; onUpgradeClick: () => void; accent: string; loading?: boolean;
}> = ({ used, limit, tier, aiEnabled, onUpgradeClick, accent, loading = false }) => {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const isNear = pct >= 80; const isFull = pct >= 100;
  const fmtNum = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n));
  const tierAccent = tier === 'pro' ? '#FBBF24' : tier === 'power' ? '#A78BFA' : 'rgba(255,255,255,.3)';
  return (
    <div className="flex items-center gap-2 text-[10px]" style={{ fontFamily: SANS, fontWeight: 200 }}>
      <span className="px-1.5 py-0.5 rounded-md text-[9px] font-semibold" style={{ background: `${tierAccent}20`, color: tierAccent }}>
        {TIER_DISPLAY_NAME[tier].toUpperCase()}
      </span>
      <Database size={10} className="text-white/20" />
      <div className="w-16 h-1 rounded-full bg-white/5">
        <div className="h-1 rounded-full transition-all" style={{ width: `${pct}%`, background: isFull ? '#EF4444' : isNear ? '#FBBF24' : accent }} />
      </div>
      {loading ? (
        <span className="text-white/20">Calculating indexed items...</span>
      ) : (
        <span className={isFull ? 'text-red-400/70' : isNear ? 'text-yellow-400/70' : 'text-white/20'}>
          {fmtNum(used)}/{fmtNum(limit)} indexed
        </span>
      )}
      {aiEnabled && <span className="text-emerald-400/50 flex items-center gap-0.5"><Sparkles size={8} />AI</span>}
      {(isNear || tier === 'free') && (
        <button
          onClick={(e) => { e.stopPropagation(); onUpgradeClick(); }}
          className="text-yellow-400/50 hover:text-yellow-400 flex items-center gap-0.5 transition-colors cursor-pointer pointer-events-auto"
          type="button"
        >
          <Zap size={8} />{isFull ? 'Full' : 'Upgrade'}
        </button>
      )}
    </div>
  );
};

const CollectionsModal: React.FC<{
  isOpen: boolean;
  collections: CollectionModel[];
  selectedCollectionId: string | null;
  currentItem?: MediaItem;
  enabled: boolean;
  limit: number;
  onClose: () => void;
  onCreate: (name: string) => Promise<void>;
  onRename: (id: string, name: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onToggleCurrentItem: (collectionId: string, shouldAdd: boolean) => Promise<void>;
  onSelectCollection: (collectionId: string | null) => void;
  onUpgrade: () => void;
}> = ({
  isOpen, collections, selectedCollectionId, currentItem, enabled, limit, onClose, onCreate, onRename, onDelete, onToggleCurrentItem, onSelectCollection, onUpgrade,
}) => {
  const [newName, setNewName] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  if (!isOpen) return null;
  const atLimit = limit >= 0 && collections.length >= limit;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/65 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-xl glass-modal rounded-3xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl text-white font-light" style={{ fontFamily: SERIF }}>Collections</h3>
          <button onClick={onClose} className="text-white/25 hover:text-white"><X size={18} /></button>
        </div>
        {!enabled ? (
          <div className="rounded-2xl glass-panel p-4 mb-4 text-sm text-white/50" style={{ fontFamily: SANS }}>
            Collections are disabled on your current plan.
            <button className="ml-2 text-yellow-300/80 hover:text-yellow-200" onClick={onUpgrade}>Upgrade</button>
          </div>
        ) : (
          <div className="rounded-2xl glass-panel p-4 mb-4">
            <div className="flex gap-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="New collection name"
                className="flex-1 glass-input rounded-xl px-3 py-2 text-sm"
              />
              <button
                className="px-3 py-2 rounded-xl text-black text-sm font-medium disabled:opacity-50"
                style={{ background: '#C4B5FD' }}
                disabled={!newName.trim() || atLimit}
                onClick={async () => {
                  const name = newName.trim();
                  if (!name) return;
                  setBusyId('create');
                  try {
                    await onCreate(name);
                    setNewName('');
                  } finally {
                    setBusyId(null);
                  }
                }}
              >
                {busyId === 'create' ? 'Creating...' : 'Create'}
              </button>
            </div>
            {atLimit && <p className="text-xs text-yellow-300/70 mt-2">Collection limit reached ({limit}).</p>}
          </div>
        )}
        <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
          <button
            className={`w-full text-left px-3 py-2 rounded-xl text-sm ${selectedCollectionId === null ? 'bg-white/15 text-white' : 'glass-panel text-white/60 hover:text-white'}`}
            onClick={() => onSelectCollection(null)}
          >
            All indexed items
          </button>
          {collections.map((col) => {
            const currentInCollection = Boolean(currentItem?.collectionIds?.includes(col.id));
            return (
              <div key={col.id} className="rounded-xl glass-panel px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <button
                    className={`text-left text-sm flex-1 ${selectedCollectionId === col.id ? 'text-white' : 'text-white/70 hover:text-white'}`}
                    onClick={() => onSelectCollection(col.id)}
                  >
                    {col.name}
                    <span className="ml-2 text-[10px] text-white/30">{col.itemCount} items</span>
                  </button>
                  {currentItem && (
                    <button
                      className="text-[10px] px-2 py-1 rounded-lg bg-white/10 text-white/70 hover:text-white"
                      onClick={async () => {
                        setBusyId(col.id);
                        try {
                          await onToggleCurrentItem(col.id, !currentInCollection);
                        } finally {
                          setBusyId(null);
                        }
                      }}
                      disabled={busyId === col.id}
                    >
                      {busyId === col.id ? 'Saving...' : currentInCollection ? 'Remove item' : 'Add item'}
                    </button>
                  )}
                </div>
                <div className="flex gap-2 mt-2">
                  <button
                    className="text-[10px] text-white/45 hover:text-white"
                    onClick={async () => {
                      const next = window.prompt('Rename collection', col.name)?.trim();
                      if (!next || next === col.name) return;
                      await onRename(col.id, next);
                    }}
                  >
                    Rename
                  </button>
                  <button
                    className="text-[10px] text-rose-300/65 hover:text-rose-200"
                    onClick={async () => {
                      if (!window.confirm(`Delete "${col.name}"?`)) return;
                      await onDelete(col.id);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const SignOutConfirmModal: React.FC<{
  isOpen: boolean;
  cloudBackupEnabled: boolean;
  onCancel: () => void;
  onSignOutOnly: () => void;
  onExportAndSignOut: () => void;
}> = ({ isOpen, cloudBackupEnabled, onCancel, onSignOutOnly, onExportAndSignOut }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onCancel} />
      <div className="relative w-full max-w-lg glass-modal rounded-3xl p-6">
        <h3 className="text-2xl font-light text-white mb-3" style={{ fontFamily: SERIF }}>Before signing out...</h3>
        <p className="text-sm text-white/55 mb-6" style={{ fontFamily: SANS }}>
          {cloudBackupEnabled
            ? 'Export your local media manifest? Cloud backup is enabled, so export is optional but recommended.'
            : 'Export your local media manifest? Local-only files are cleared on sign out unless cloud backup is enabled.'}
        </p>
        <div className="flex flex-wrap gap-2 justify-end">
          <button onClick={onCancel} className="px-3 py-2 rounded-xl glass-panel text-white/50 hover:text-white text-sm">Cancel</button>
          <button onClick={onSignOutOnly} className="px-3 py-2 rounded-xl glass-panel text-rose-300/70 hover:text-rose-200 text-sm">Sign out without exporting</button>
          <button onClick={onExportAndSignOut} className="px-3 py-2 rounded-xl text-sm font-medium text-black" style={{ background: '#C4B5FD' }}>
            Export & Sign out
          </button>
        </div>
      </div>
    </div>
  );
};


// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
//  MAIN COMPONENT - Full App replacement with Ethereal Glass skin
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const PlayerDesign2: React.FC = () => {
  // â”€â”€â”€ Auth State â”€â”€â”€
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [authSession, setAuthSession] = useState<DriveAuthSession | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  // â”€â”€â”€ Data State â”€â”€â”€
  const [libraryItems, setLibraryItems] = useState<MediaItem[]>([]);
  const [localItems, setLocalItems] = useState<MediaItem[]>([]);
  const [uploadThingItems, setUploadThingItems] = useState<MediaItem[]>([]);
  const [unsyncedSessionLocalIds, setUnsyncedSessionLocalIds] = useState<string[]>([]);
  const [hasNewLocalUploadsThisSession, setHasNewLocalUploadsThisSession] = useState(false);
  const [shuffledQueue, setShuffledQueue] = useState<MediaItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // â”€â”€â”€ Plan / Subscription State â”€â”€â”€
  const [planData, setPlanData] = useState<PlanResponse | null>(null);
  const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier>('free');
  const [cloudBackupEnabled, setCloudBackupEnabled] = useState(false);
  const [cloudBackupUnavailableMessage, setCloudBackupUnavailableMessage] = useState<string | null>(null);
  const [isRestoringCloudBackup, setIsRestoringCloudBackup] = useState(false);
  const [dropboxConnected, setDropboxConnected] = useState(false);
  const [dropboxCursorPath, setDropboxCursorPath] = useState('');
  const [collections, setCollections] = useState<CollectionModel[]>([]);
  const [collectionsLimit, setCollectionsLimit] = useState(0);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);

  // â”€â”€â”€ Search State â”€â”€â”€
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<MediaItem[]>([]);

  // â”€â”€â”€ Playback State â”€â”€â”€
  const [settings, setSettings] = useState<AppSettings>({ filter: MediaType.ALL, interval: 5, isPlaying: false, showInfo: true });
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);

  // â”€â”€â”€ UI State â”€â”€â”€
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showPasteLinks, setShowPasteLinks] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('');
  const [targetUpgradeTier, setTargetUpgradeTier] = useState<SubscriptionTier>('pro');
  const [showCollections, setShowCollections] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showDesktopMoreMenu, setShowDesktopMoreMenu] = useState(false);
  const [showSourceMenu, setShowSourceMenu] = useState(false);
  const [showDropboxImportModal, setShowDropboxImportModal] = useState(false);
  const [dropboxImportPath, setDropboxImportPath] = useState('/');
  const [dropboxImportRecursive, setDropboxImportRecursive] = useState(true);
  const [dropboxImportLoading, setDropboxImportLoading] = useState(false);
  const [dropboxImportError, setDropboxImportError] = useState<string | null>(null);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [liked, setLiked] = useState<Set<string>>(new Set());
  const [loadStatusMessage, setLoadStatusMessage] = useState<string | null>(null);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches : false,
  );

  // â”€â”€â”€ AI Analysis State â”€â”€â”€
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // â”€â”€â”€ Slideshow â”€â”€â”€
  const [progress, setProgress] = useState(0);
  const slideshowTimerRef = useRef<number | null>(null);
  const progressRef = useRef<number | null>(null);
  const progressStartRef = useRef<number | null>(null);
  const shuffledQueueRef = useRef<MediaItem[]>([]);
  const cloudRestoreAttemptedRef = useRef(false);
  const dropboxSyncAttemptedRef = useRef(false);
  const uploadThingBootstrapRef = useRef(false);
  const initStartedRef = useRef(false);
  const restoreToastShownRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hideTimerRef = useRef<number | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const desktopMoreMenuRef = useRef<HTMLDivElement>(null);
  const sourceMenuRef = useRef<HTMLDivElement>(null);

  const HEIC_PREFETCH_COUNT = 4;

  // â”€â”€â”€ Derived â”€â”€â”€
  const aiEnabled = planData?.features?.aiSearch ?? false;
  const collectionsEnabled = planData?.features?.collections ?? false;
  const driveItemsUsed = Math.max(planData?.usage?.driveItemsUsed ?? 0, libraryItems.length + uploadThingItems.length);
  const driveItemsLimit = planData?.usage?.driveItemsLimit ?? 2000;
  const currentTier: PlanTier = planData?.tier ?? 'free';
  const usageLoading = isAuthenticated && !planData && loading;
  const currentItem = shuffledQueue[currentIndex];
  const accent = accentFor(currentIndex);

  // â”€â”€â”€ Upgrade handler â”€â”€â”€
  const handleUpgradeClick = useCallback(() => {
    // Check authentication status instead of just userId
    if (!isAuthenticated || !authSession) {
      toast.info('Please sign in to upgrade your plan.', { duration: 4000 });
      return;
    }

    // Derive userId from session if not in state (shouldn't happen, but safety check)
    let effectiveUserId = userId;
    if (!effectiveUserId && authSession) {
      effectiveUserId = authSession.userSub || `user-${btoa(authSession.accessToken).substring(0, 16)}`;
      setUserId(effectiveUserId);
    }

    if (!effectiveUserId) {
      toast.error('Unable to determine user ID. Please try again.', { duration: 4000 });
      return;
    }

    const targetTier: SubscriptionTier = currentTier === 'free' ? 'pro' : currentTier === 'pro' ? 'power' : 'pro';
    const reason = currentTier === 'free'
      ? 'Upgrade to Pro for more Drive storage, AI search, and collections.'
      : currentTier === 'pro'
        ? 'Upgrade to Power for unlimited storage and premium features.'
        : 'Upgrade your plan for more features.';
    setTargetUpgradeTier(targetTier);
    setUpgradeReason(reason);
    setShowUpgradePrompt(true);
  }, [currentTier, userId, isAuthenticated, authSession]);

  const isHeicItem = useCallback((item: MediaItem) => (
    item.mimeType === 'image/heic' || item.mimeType === 'image/heif' ||
    item.mimeType === 'image/heic-sequence' || item.mimeType === 'image/heif-sequence' ||
    item.name.toLowerCase().endsWith('.heic') || item.name.toLowerCase().endsWith('.heif')
  ), []);

  // â”€â”€â”€ Shuffle helper â”€â”€â”€
  const shuffleArray = <T,>(arr: T[]): T[] => {
    const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[a[i], a[j]] = [a[j], a[i]]; } return a;
  };
  const applyCollectionScope = useCallback((items: MediaItem[]): MediaItem[] => {
    if (!selectedCollectionId) return items;
    return items.filter((item) => item.collectionIds?.includes(selectedCollectionId));
  }, [selectedCollectionId]);

  const reshuffle = useCallback((dItems: MediaItem[], lItems: MediaItem[], filter: MediaType) => {
    const all = applyCollectionScope([...dItems, ...lItems]);
    let filtered = all;
    if (filter === MediaType.PHOTO) filtered = all.filter(f => getMediaType(f) === 'photo');
    else if (filter === MediaType.VIDEO) filtered = all.filter(f => getMediaType(f) === 'video');
    if (filtered.length === 0) { setShuffledQueue([]); return; }
    setShuffledQueue(shuffleArray(filtered));
    setCurrentIndex(0);
  }, [applyCollectionScope]);

  const devLog = useCallback((message: string, details?: Record<string, unknown>) => {
    if (!import.meta.env.DEV) return;
    if (details) {
      console.debug(`[player] ${message}`, details);
      return;
    }
    console.debug(`[player] ${message}`);
  }, []);

  const showRestoreToastOnce = useCallback((message: string) => {
    if (restoreToastShownRef.current) return;
    restoreToastShownRef.current = true;
    try {
      sessionStorage.setItem(RESTORE_TOAST_SESSION_KEY, '1');
    } catch {}
    toast.info(message, { duration: 4000 });
  }, []);

  // â”€â”€â”€ Plan refresh â”€â”€â”€
  const refreshPlan = useCallback(async (session: DriveAuthSession, reason = 'unknown') => {
    devLog('fetch:/api/billing/plan:start', { reason });
    try {
      const d = await planService.getPlan(session);
      setPlanData(d);
      setSubscriptionTier(d.tier);
      if (!d.features.cloudBackup) {
        setCloudBackupEnabled(false);
      }
      devLog('fetch:/api/billing/plan:end', { reason, tier: d.tier });
    } catch {
      devLog('fetch:/api/billing/plan:error', { reason });
    }
  }, [devLog]);

  const loadCollections = useCallback(async (session: DriveAuthSession, reason = 'unknown') => {
    devLog('fetch:/api/collections:start', { reason });
    try {
      const data = await collectionsService.list(session);
      setCollections(data.collections);
      setCollectionsLimit(data.limit);
      setSelectedCollectionId((current) => (
        current && !data.collections.find((col) => col.id === current) ? null : current
      ));
      devLog('fetch:/api/collections:end', { reason, count: data.collections.length });
    } catch {
      setCollections([]);
      devLog('fetch:/api/collections:error', { reason });
    }
  }, [devLog]);

  const refreshUploadThingFiles = useCallback(async (session: DriveAuthSession, reason = 'unknown') => {
    devLog('fetch:/api/uploadthing:start', { reason });
    try {
      const response = await uploadthingService.list(session);
      setUploadThingItems(response.files.map(mapUploadThingFileToMediaItem));
      devLog('fetch:/api/uploadthing:end', { reason, count: response.files.length });
    } catch (error: any) {
      devLog('fetch:/api/uploadthing:error', { reason, error: error?.message || 'unknown' });
    }
  }, [devLog]);

  const fileSignature = useCallback((name: string, sizeBytes?: number) =>
    `${name.toLowerCase()}::${Number.isFinite(Number(sizeBytes)) ? Number(sizeBytes) : 0}`, []);

  const toUploadThingFile = useCallback(async (item: MediaItem): Promise<File | null> => {
    if (!authSession) return null;

    if (item.source === 'local' && item.file) {
      return item.file;
    }

    if (item.source === 'drive') {
      const response = await fetch(`https://www.googleapis.com/drive/v3/files/${item.id}?alt=media`, {
        headers: { Authorization: `Bearer ${authSession.accessToken}` },
      });
      if (!response.ok) return null;
      const blob = await response.blob();
      return new File([blob], item.name, { type: item.mimeType || blob.type || inferMimeTypeFromName(item.name) });
    }

    if (item.source === 'dropbox') {
      const response = await fetch(`/api/dropbox/temporary-link?id=${encodeURIComponent(item.id)}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authSession.idToken || authSession.accessToken}`,
          'x-access-token': authSession.accessToken,
        },
      });
      if (!response.ok) return null;
      const payload = await response.json().catch(() => ({}));
      const link = typeof payload?.link === 'string' ? payload.link : '';
      if (!link) return null;

      const mediaResponse = await fetch(link);
      if (!mediaResponse.ok) return null;
      const blob = await mediaResponse.blob();
      return new File([blob], item.name, { type: item.mimeType || blob.type || inferMimeTypeFromName(item.name) });
    }

    return null;
  }, [authSession]);

  const syncItemsToUploadThing = useCallback(async (items: MediaItem[], reason = 'unknown') => {
    if (!authSession || !cloudBackupEnabled || !planData?.features.cloudBackup) return;
    if (items.length === 0) return;

    const existingList = await uploadthingService.list(authSession);
    const existingSignatures = new Set(
      existingList.files.map((file) => fileSignature(file.name, file.size)),
    );

    let uploaded = 0;
    try {
      for (const item of items) {
        if (item.source === 'uploadthing') continue;
        const signature = fileSignature(item.name, item.sizeBytes);
        if (existingSignatures.has(signature)) continue;

        const file = await toUploadThingFile(item);
        if (!file) continue;

        await uploadthingService.uploadFile(authSession, file);
        existingSignatures.add(fileSignature(file.name, file.size));
        uploaded += 1;
      }

      await refreshUploadThingFiles(authSession, `background-sync:${reason}`);
      if (uploaded > 0) {
        toast.success(`Persisted ${uploaded} file${uploaded > 1 ? 's' : ''} for cross-session restore.`, { duration: 3500 });
      }
    } catch (error: any) {
      devLog('uploadthing:background-sync:error', { reason, error: error?.message || 'unknown' });
    }
  }, [authSession, cloudBackupEnabled, planData?.features.cloudBackup, fileSignature, toUploadThingFile, refreshUploadThingFiles, devLog]);

  const toggleFullscreen = useCallback(async () => {
    const prefersPseudo = isIOSLikeDevice() || !supportsNativeFullscreen();
    if (prefersPseudo) {
      const next = !isPseudoFullscreen;
      setIsPseudoFullscreen(next);
      try {
        sessionStorage.setItem(SESSION_FULLSCREEN_PREF_KEY, next ? 'pseudo' : 'off');
      } catch {}
      return;
    }
    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(() => {});
    } else {
      await document.documentElement.requestFullscreen().catch(() => {});
    }
  }, [isPseudoFullscreen]);

  const cacheDriveMetadata = useCallback(async (driveItems: MediaItem[], stableUserId: string) => {
    if (!stableUserId) return;
    const existingMetadata = await storageService.getMediaMetadata(stableUserId);
    const localMetadata = existingMetadata.filter((meta) => meta.source === 'local');
    const driveMetadata = driveItems
      .filter((item) => item.source !== 'local')
      .map((item) => ({
        id: item.id,
        userId: stableUserId,
        driveFileId: item.source === 'drive' ? (item.driveFile?.id || item.id) : undefined,
        name: item.name,
        mimeType: item.mimeType,
        source: item.source,
        indexedAt: new Date().toISOString(),
        thumbnailUrl: item.source === 'drive' ? item.driveFile?.thumbnailLink : item.dropboxFile?.thumbnailLink,
        externalLink: item.source === 'dropbox' ? item.dropboxFile?.directLink : undefined,
      }));
    await storageService.saveMediaMetadata([...localMetadata, ...driveMetadata]);
  }, []);

  // â”€â”€â”€ Hydrate library from server â”€â”€â”€
  const hydrateFromServerLibrary = useCallback(async (session: DriveAuthSession, legacyUserId: string | null, reason = 'unknown') => {
    devLog('fetch:/api/library:start', { reason });
    const authorized = await libraryService.getLibrary(session);
    let driveItems: MediaItem[] = authorized.map(mapAuthorizedItemToMediaItem);

    // If server data is temporarily unavailable, restore from cached metadata.
    if (driveItems.length === 0 && legacyUserId) {
      const cachedMetadata = await storageService.getMediaMetadata(legacyUserId);
      const cachedDriveItems: MediaItem[] = cachedMetadata
        .filter((meta) => meta.source !== 'local')
        .map((meta) => ({
          id: meta.driveFileId || meta.id,
          name: meta.name,
          mimeType: meta.mimeType,
          source: meta.source === 'dropbox' ? 'dropbox' : 'drive',
          driveFile: meta.source === 'dropbox'
            ? undefined
            : {
                id: meta.driveFileId || meta.id,
                name: meta.name,
                mimeType: meta.mimeType,
                thumbnailLink: meta.thumbnailUrl,
              },
          dropboxFile: meta.source === 'dropbox' && meta.externalLink
            ? {
                id: meta.id,
                name: meta.name,
                mimeType: meta.mimeType,
                directLink: meta.externalLink,
                thumbnailLink: meta.thumbnailUrl,
              }
            : undefined,
        }));

      if (cachedDriveItems.length > 0) {
        driveItems = cachedDriveItems;
        showRestoreToastOnce(`Restored ${cachedDriveItems.length} Drive items from local metadata cache.`);
      }
    }

    setLibraryItems(driveItems);
    reshuffle([...driveItems, ...uploadThingItems], localItems, settings.filter);
    if (legacyUserId && driveItems.length > 0) {
      await cacheDriveMetadata(driveItems, legacyUserId);
    }
    devLog('fetch:/api/library:end', { reason, count: driveItems.length });
  }, [cacheDriveMetadata, localItems, settings.filter, reshuffle, showRestoreToastOnce, devLog, uploadThingItems]);

  const uploadLocalFilesToCloudBackup = useCallback(async (items: MediaItem[]) => {
    if (!authSession || !cloudBackupEnabled || !planData?.features.cloudBackup || cloudBackupUnavailableMessage) return;
    const localWithFiles = items.filter((item) => item.source === 'local' && item.file);
    if (localWithFiles.length === 0) return;

    const metadata = userId ? await storageService.getMediaMetadata(userId) : [];
    const hashById = new Map(
      metadata
        .filter((entry) => entry.source === 'local' && entry.localFileHash)
        .map((entry) => [entry.id, entry.localFileHash as string]),
    );

    let skippedLarge = 0;
    let blockedByQuota = false;
    const uploadPromises = localWithFiles.map(async (item) => {
      if (blockedByQuota) return;
      const file = item.file as File;
      if (file.size > MAX_CLOUD_BACKUP_BYTES) {
        skippedLarge += 1;
        return;
      }
      const hash = hashById.get(item.id) || await storageService.calculateFileHash(file);
      try {
        await cloudBackupService.uploadLocalFile(authSession, { id: item.id, hash, file });
      } catch (error: any) {
        if (error instanceof CloudBackupApiError && (error.code === 'BLOB_DISABLED' || error.status === 503 || error.status === 429)) {
          blockedByQuota = true;
          setCloudBackupUnavailableMessage('Cloud backup is temporarily unavailable due to storage request limits. Your local/Drive/Dropbox library still works.');
          setCloudBackupEnabled(false);
        }
        throw error;
      }
    });

    const results = await Promise.allSettled(uploadPromises);
    const failures = results.filter((result) => result.status === 'rejected').length;
    if (blockedByQuota) {
      toast.error('Cloud backup is temporarily unavailable due to storage request limits. Your local/Drive/Dropbox library still works.', { duration: 6500 });
      return;
    }
    if (failures > 0) {
      toast.error(`Cloud backup skipped ${failures} file${failures > 1 ? 's' : ''}.`, { duration: 4000 });
    }
    if (skippedLarge > 0) {
      toast.info(`Skipped ${skippedLarge} file${skippedLarge > 1 ? 's' : ''} over ${Math.round(MAX_CLOUD_BACKUP_BYTES / (1024 * 1024))}MB.`, { duration: 4500 });
    }
  }, [authSession, cloudBackupEnabled, planData?.features.cloudBackup, userId, cloudBackupUnavailableMessage]);

  const restoreLocalFromCloudBackup = useCallback(async (session: DriveAuthSession, stableUserId: string) => {
    if (!cloudBackupEnabled || !planData?.features.cloudBackup || isRestoringCloudBackup || cloudBackupUnavailableMessage) return;
    setIsRestoringCloudBackup(true);
    setLoadStatusMessage('Syncing from cloud...');
    try {
      const backups = await cloudBackupService.listFiles(session);
      if (backups.length === 0) return;

      const metadataMap = new Map(
        (await storageService.getMediaMetadata(stableUserId)).map((meta) => [meta.id, meta]),
      );
      const restored: MediaItem[] = [];

      for (const backup of backups) {
        try {
          const existing = await storageService.getLocalFile(backup.hash);
          if (existing) continue;

          const restoredFile = await cloudBackupService.downloadLocalFile(session, backup);
          await storageService.saveLocalFile(backup.hash, restoredFile, stableUserId);
          const itemId = backup.id || `local-${backup.hash.substring(0, 16)}`;
          restored.push({
            id: itemId,
            name: restoredFile.name,
            mimeType: restoredFile.type || inferMimeTypeFromName(restoredFile.name),
            source: 'local',
            file: restoredFile,
          });
          metadataMap.set(itemId, {
            id: itemId,
            userId: stableUserId,
            localFileHash: backup.hash,
            name: restoredFile.name,
            mimeType: restoredFile.type || inferMimeTypeFromName(restoredFile.name),
            fileSize: restoredFile.size,
            source: 'local',
            indexedAt: new Date().toISOString(),
          });
        } catch (err) {
          console.warn('Failed to restore cloud backup file', backup.name, err);
        }
      }

      if (restored.length > 0) {
        await storageService.saveMediaMetadata(Array.from(metadataMap.values()));
        setLocalItems((prev) => {
          const map = new Map(prev.map((item) => [item.id, item]));
          restored.forEach((item) => map.set(item.id, item));
          return Array.from(map.values());
        });
        toast.success(`Restored ${restored.length} local file${restored.length > 1 ? 's' : ''} from cloud backup.`, { duration: 4500 });
      }
    } catch (error: any) {
      if (error instanceof CloudBackupApiError && (error.code === 'BLOB_DISABLED' || error.status === 503 || error.status === 429)) {
        setCloudBackupUnavailableMessage('Cloud backup is temporarily unavailable due to storage request limits. Your local/Drive/Dropbox library still works.');
        setCloudBackupEnabled(false);
        return;
      }
      throw error;
    } finally {
      setIsRestoringCloudBackup(false);
      setLoadStatusMessage(null);
    }
  }, [cloudBackupEnabled, isRestoringCloudBackup, planData?.features.cloudBackup, cloudBackupUnavailableMessage]);

  const exportLocalManifest = useCallback(async () => {
    if (!userId) return;
    const metadata = await storageService.getMediaMetadata(userId);
    const localMetadata = metadata.filter((entry) => entry.source === 'local');
    if (localMetadata.length === 0) return;

    const payload = {
      exportedAt: new Date().toISOString(),
      note: 'Local-only media manifest export. Use this to track files before sign-out.',
      files: localMetadata.map((entry) => ({
        id: entry.id,
        name: entry.name,
        mimeType: entry.mimeType,
        fileSize: entry.fileSize,
        localFileHash: entry.localFileHash,
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `refract-local-export-${new Date().toISOString().slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [userId]);

  const handleCloudBackupToggle = useCallback(async (enabled: boolean) => {
    if (!authSession) {
      toast.error('Sign in is required to configure cloud backup.');
      return;
    }
    if (enabled && !planData?.features.cloudBackup) {
      setUpgradeReason('Cloud backup is available on Pro and Power plans.');
      setTargetUpgradeTier('pro');
      setShowUpgradePrompt(true);
      return;
    }

    // Disabling now permanently deletes the backed-up files, so confirm first
    // and tell the user how many they are about to lose.
    if (!enabled) {
      let backedUpCount = 0;
      try {
        backedUpCount = (await cloudBackupService.listFiles(authSession)).length;
      } catch {
        // Count is only for the prompt; fall back to a generic warning.
      }
      const detail = backedUpCount > 0
        ? `This permanently deletes ${backedUpCount} backed-up ${backedUpCount === 1 ? 'file' : 'files'} from cloud storage.`
        : 'This permanently deletes any backed-up files from cloud storage.';
      if (!window.confirm(`Turn off Cloud Backup?\n\n${detail} Your local and Drive files are not affected.`)) {
        return;
      }
    }

    try {
      const settings = await cloudBackupService.setEnabled(authSession, enabled);
      setCloudBackupEnabled(Boolean(settings.enabled));
      if (settings.enabled) {
        setCloudBackupUnavailableMessage(null);
      }
      if (settings.enabled && localItems.length > 0) {
        toast.loading('Uploading existing local files to Drive for cross-device access...', { id: 'cloud-backup-upload' });
        const stableUserId = userId || authSession.userSub || `user-${btoa(authSession.accessToken).substring(0, 16)}`;
        await syncLocalItemsToDrive(localItems, stableUserId);
        await syncItemsToUploadThing([...libraryItems, ...localItems], 'cloud-backup-enabled');
        toast.success('Cloud storage enabled for local files.', { id: 'cloud-backup-upload', duration: 3500 });
      } else {
        if (settings.enabled) {
          await syncItemsToUploadThing([...libraryItems, ...localItems], 'cloud-backup-enabled');
          toast.success('Cloud storage enabled.', { duration: 3000 });
        } else {
          const deleted = settings.deletedCount ?? 0;
          toast.success(
            deleted > 0
              ? `Cloud backup disabled — ${deleted} ${deleted === 1 ? 'file' : 'files'} deleted.`
              : 'Cloud backup disabled.',
            { duration: 3000 },
          );
        }
      }
    } catch (err: any) {
      if (err instanceof CloudBackupApiError && (err.code === 'BLOB_DISABLED' || err.status === 503 || err.status === 429)) {
        setCloudBackupUnavailableMessage('Cloud backup is temporarily unavailable due to storage request limits. Your local/Drive/Dropbox library still works.');
        setCloudBackupEnabled(false);
        toast.error('Cloud backup is temporarily unavailable due to storage request limits. Your local/Drive/Dropbox library still works.', { duration: 6500 });
        return;
      }
      toast.error(err?.message || 'Failed to update cloud backup setting.');
    }
  }, [authSession, planData?.features.cloudBackup, localItems, userId, libraryItems, syncItemsToUploadThing]);

  // â”€â”€â”€ Initialization â”€â”€â”€
  useEffect(() => {
    try {
      restoreToastShownRef.current = sessionStorage.getItem(RESTORE_TOAST_SESSION_KEY) === '1';
    } catch {
      restoreToastShownRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    const init = async () => {
      try {
        await initGapiClient();
        const stored = await storageService.getAuthSession();
        const bypassSession = !stored?.accessToken && isDevAuthBypassEnabled() ? getDevAuthSession() : null;
        const effective = stored?.accessToken
          ? stored
          : bypassSession
            ? {
                accessToken: bypassSession.accessToken,
                idToken: bypassSession.idToken,
                userSub: bypassSession.userSub,
                email: bypassSession.email,
              }
            : null;

        if (!effective?.accessToken) {
          devLog('init:no-auth-session');
          return;
        }

        devLog('init:auth-session-restored', { source: bypassSession ? 'dev-bypass' : 'storage' });
        const session: DriveAuthSession = {
          accessToken: effective.accessToken,
          idToken: effective.idToken,
          expiresAt: Date.now() + 30 * 60 * 1000,
          userSub: effective.userSub,
          email: effective.email,
        };
        setAuthSession(session);
        setAccessToken(effective.accessToken);
        setIsAuthenticated(true);
        if (window.gapi?.client) window.gapi.client.setToken({ access_token: effective.accessToken });

        const stableUserId = effective.userSub || `user-${btoa(effective.accessToken).substring(0, 16)}`;
        setUserId(stableUserId);
        if (effective.userSub) setSentryAuthContext(effective.userSub);
        await refreshPlan(session, 'init');
        await loadCollections(session, 'init');
        await refreshUploadThingFiles(session, 'init');

        setLoadStatusMessage('Loading from your local metadata cache...');
        const localMeta = await storageService.getMediaMetadata(stableUserId);
        const localOnly = localMeta.filter(m => m.source === 'local');
        const restored: MediaItem[] = [];
        for (const meta of localOnly) {
          if (!meta.localFileHash) continue;
          try {
            const file = await storageService.getLocalFile(meta.localFileHash);
            if (file) {
              restored.push({
                id: meta.id,
                name: meta.name,
                mimeType: meta.mimeType,
                source: 'local',
                file,
                contentHash: meta.localFileHash,
                sizeBytes: typeof meta.fileSize === 'number' ? meta.fileSize : file.size,
                originalSource: 'local',
                syncStatus: 'device-only',
              });
            }
          }
          catch { }
        }
        setLocalItems(restored);
        await hydrateFromServerLibrary(session, stableUserId, 'init');
        setLoadStatusMessage(null);
      } catch (err: any) {
        setError(err?.message || 'Failed to initialize.');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [hydrateFromServerLibrary, loadCollections, refreshPlan, refreshUploadThingFiles, devLog]);

  useEffect(() => {
    cloudRestoreAttemptedRef.current = false;
    dropboxSyncAttemptedRef.current = false;
    uploadThingBootstrapRef.current = false;
  }, [userId]);

  useEffect(() => {
    const loadCloudBackupSettings = async () => {
      if (!authSession || !isAuthenticated || !planData?.features.cloudBackup) return;
      try {
        const settings = await cloudBackupService.getSettings(authSession);
        setCloudBackupEnabled(Boolean(settings.enabled));
        if (settings.enabled) {
          setCloudBackupUnavailableMessage(null);
        }
      } catch (error: any) {
        if (error instanceof CloudBackupApiError && (error.code === 'BLOB_DISABLED' || error.status === 503 || error.status === 429)) {
          setCloudBackupUnavailableMessage('Cloud backup is temporarily unavailable due to storage request limits. Your local/Drive/Dropbox library still works.');
        }
        setCloudBackupEnabled(false);
      }
    };
    loadCloudBackupSettings();
  }, [authSession, isAuthenticated, planData?.features.cloudBackup]);

  useEffect(() => {
    if (!authSession || !isAuthenticated) return;
    if (!cloudBackupEnabled || !planData?.features.cloudBackup) return;
    if (uploadThingBootstrapRef.current) return;

    uploadThingBootstrapRef.current = true;
    syncItemsToUploadThing([...libraryItems, ...localItems], 'plan-upgrade-bootstrap').catch(() => {
      uploadThingBootstrapRef.current = false;
    });
  }, [authSession, isAuthenticated, cloudBackupEnabled, planData?.features.cloudBackup, libraryItems, localItems, syncItemsToUploadThing]);

  useEffect(() => {
    const loadDropboxConnection = async () => {
      if (!authSession || !isAuthenticated || !isDropboxConfigured()) {
        setDropboxConnected(false);
        setDropboxCursorPath('');
        return;
      }
      try {
        devLog('fetch:/api/dropbox/session:start', { reason: 'auth-ready' });
        const status = await getDropboxSession(authSession);
        setDropboxConnected(Boolean(status.connected));
        setDropboxCursorPath(status.cursorPath || '');
        devLog('fetch:/api/dropbox/session:end', { reason: 'auth-ready', connected: Boolean(status.connected) });
      } catch {
        setDropboxConnected(false);
        setDropboxCursorPath('');
        devLog('fetch:/api/dropbox/session:error', { reason: 'auth-ready' });
      }
    };
    loadDropboxConnection();
  }, [authSession, isAuthenticated, devLog]);

  useEffect(() => {
    if (!collectionsEnabled && selectedCollectionId) {
      setSelectedCollectionId(null);
    }
  }, [collectionsEnabled, selectedCollectionId]);

  useEffect(() => {
    if (!authSession || !dropboxConnected || !isAuthenticated) return;
    if (dropboxSyncAttemptedRef.current) return;
    dropboxSyncAttemptedRef.current = true;
    syncDropboxChanges().catch(() => {});
  }, [authSession, dropboxConnected, isAuthenticated]);

  useEffect(() => {
    if (!authSession || !userId || !cloudBackupEnabled || !planData?.features.cloudBackup) return;
    if (localItems.length > 0 || cloudRestoreAttemptedRef.current) return;
    cloudRestoreAttemptedRef.current = true;
    restoreLocalFromCloudBackup(authSession, userId).catch(() => {});
  }, [authSession, userId, cloudBackupEnabled, planData?.features.cloudBackup, localItems.length, restoreLocalFromCloudBackup]);

  useEffect(() => { shuffledQueueRef.current = shuffledQueue; }, [shuffledQueue]);
  useEffect(() => {
    setHasNewLocalUploadsThisSession(unsyncedSessionLocalIds.length > 0);
  }, [unsyncedSessionLocalIds]);
  useEffect(() => { setIsMediaLoaded(false); }, [currentIndex]);
  useEffect(() => {
    if (!isAuthenticated) return;
    if (libraryItems.length === 0 && localItems.length === 0) return;
    storageService.saveMediaItems([...libraryItems, ...localItems]).catch(() => { });
  }, [isAuthenticated, libraryItems, localItems]);

  // â”€â”€â”€ Preloading â”€â”€â”€
  useEffect(() => {
    if (shuffledQueue.length === 0) return;
    const toPreload = []; const keepIds = new Set<string>();
    const cur = shuffledQueue[currentIndex];
    if (cur) { toPreload.push(cur); keepIds.add(cur.id); }
    for (let i = 1; i <= 4; i++) { const n = shuffledQueue[(currentIndex + i) % shuffledQueue.length]; if (n) { toPreload.push(n); keepIds.add(n.id); } }
    const prev = shuffledQueue[(currentIndex - 1 + shuffledQueue.length) % shuffledQueue.length];
    if (prev) keepIds.add(prev.id);
    toPreload.forEach(item => { if (!mediaCache.has(item.id)) mediaCache.preload(item, accessToken); });
    for (let i = 1; i <= HEIC_PREFETCH_COUNT; i++) {
      const n = shuffledQueue[(currentIndex + i) % shuffledQueue.length];
      if (!n || !isHeicItem(n)) continue;
      if (n.source !== 'drive') continue;
      if (!accessToken) continue;
      mediaCache.loadProcessedImages(n, accessToken).then(images => imageProcessingService.revokeUrls(images)).catch(() => { });
    }
    mediaCache.prune(Array.from(keepIds));
  }, [currentIndex, shuffledQueue, accessToken, isHeicItem]);

  // â”€â”€â”€ Filter changes â”€â”€â”€
  useEffect(() => {
    const t = setTimeout(() => {
      if (libraryItems.length === 0 && uploadThingItems.length === 0 && localItems.length === 0 && searchResults.length === 0) return;
      if (isSearchMode) { const d = searchResults.filter(i => i.source !== 'local'); const l = searchResults.filter(i => i.source === 'local'); reshuffle(d, l, settings.filter); }
      else reshuffle([...libraryItems, ...uploadThingItems], localItems, settings.filter);
    }, 0);
    return () => clearTimeout(t);
  }, [settings.filter, selectedCollectionId, uploadThingItems]);

  // â”€â”€â”€ Auth Handlers â”€â”€â”€
  const handleLogin = async () => {
    setError(null);
    try {
      const session = await signInToGoogle();
      setAuthSession(session); setAccessToken(session.accessToken); setIsAuthenticated(true);
      if (window.gapi?.client) window.gapi.client.setToken({ access_token: session.accessToken });
      await storageService.saveAuth({ accessToken: session.accessToken, idToken: session.idToken, userSub: session.userSub, email: session.email });
      // Use Google's stable `sub` for consistent cross-session identity
      const stableUserId = session.userSub || `user-${btoa(session.accessToken).substring(0, 16)}`;
      setUserId(stableUserId);
      if (session.userSub) setSentryAuthContext(session.userSub);
      await refreshPlan(session, 'login');
      await loadCollections(session, 'login');
      await refreshUploadThingFiles(session, 'login');
      setLoadStatusMessage('Loading from your local metadata cache...');
      await hydrateFromServerLibrary(session, stableUserId, 'login');
      setLoadStatusMessage(null);
    } catch (err: any) { setError(err.message || 'Failed to sign in'); }
  };

  const performLogout = async (exportFirst: boolean) => {
    if (exportFirst) {
      try {
        await exportLocalManifest();
      } catch {}
    }
    try {
      if (userId) {
        await storageService.clearLocalData(userId);
      }
      await storageService.clearMedia();
    } catch {}

    try { await signOut(); } catch { }
    try { await storageService.clearAuth(); } catch { }
    setIsAuthenticated(false); setAccessToken(null); setAuthSession(null);
    setLibraryItems([]); setLocalItems([]); setShuffledQueue([]);
    setUploadThingItems([]);
    setUnsyncedSessionLocalIds([]); setHasNewLocalUploadsThisSession(false);
    setIsSearchMode(false); setPlanData(null); setCloudBackupEnabled(false); setDropboxConnected(false); setDropboxCursorPath(''); clearSentryAuthContext();
    stopSlideShow();
    // Navigate back to home page - use replace to avoid adding to history
    window.location.replace('/');
  };

  const handleLogout = async () => {
    if (!shouldShowSignOutExportModal(hasNewLocalUploadsThisSession)) {
      await performLogout(false);
      return;
    }
    setShowSignOutModal(true);
  };

  const handleDeleteAccount = useCallback(async () => {
    if (!authSession) {
      toast.error('Sign in is required to delete your account.');
      return;
    }

    try {
      const result = await accountService.deleteAccount(authSession);

      if (!result.deleted) {
        // Partial deletion: say so plainly instead of implying a clean wipe.
        toast.error(result.message, { duration: 9000 });
        return;
      }

      toast.success(result.message, { duration: 6000 });
      // The account no longer exists, so the session must not survive it.
      await performLogout(false);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete account.', { duration: 7000 });
    }
  }, [authSession]);

  const syncLocalItemsToDrive = useCallback(async (items: MediaItem[], stableUserId: string) => {
    if (!authSession || !cloudBackupEnabled || !planData?.features.cloudBackup) return;

    const syncedLocalIds: string[] = [];
    const syncedDriveItems: MediaItem[] = [];
    let failed = 0;

    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      if (!item.file || !item.contentHash) continue;

      const toastId = `local-drive-sync-${item.id}`;
      toast.loading(`Uploading to Drive for cross-device access... (${index + 1}/${items.length})`, { id: toastId });
      try {
        const payload = await syncLocalFileToDriveAndLibrary({
          session: authSession,
          file: item.file,
          contentHash: item.contentHash,
          mimeType: item.mimeType,
          upsertLibrary: libraryService.upsertLibrary,
        });

        syncedLocalIds.push(item.id);
        syncedDriveItems.push({
          id: payload.fileId,
          name: payload.name,
          mimeType: payload.mimeType,
          source: 'drive',
          driveFile: {
            id: payload.fileId,
            name: payload.name,
            mimeType: payload.mimeType,
            thumbnailLink: payload.thumbnailLink,
          },
          contentHash: payload.contentHash,
          sizeBytes: payload.sizeBytes,
          originalSource: 'local',
          syncStatus: 'synced',
        });
        await storageService.deleteLocalFile(item.contentHash).catch(() => {});
        toast.success(`Synced \"${item.name}\" to Drive.`, { id: toastId, duration: 2600 });
      } catch (error: any) {
        failed += 1;
        toast.error(`\"${item.name}\" is device-only (not synced). Retry when ready.`, { id: toastId, duration: 5500 });
      }
    }

    if (syncedDriveItems.length > 0) {
      let mergedLibrary: MediaItem[] = [];
      let remainingLocal: MediaItem[] = [];

      setLibraryItems((prev) => {
        const map = new Map(prev.map((entry) => [entry.id, entry]));
        for (const syncedItem of syncedDriveItems) {
          map.set(syncedItem.id, syncedItem);
        }
        mergedLibrary = Array.from(map.values());
        return mergedLibrary;
      });

      setLocalItems((prev) => {
        remainingLocal = prev.filter((entry) => !syncedLocalIds.includes(entry.id));
        return remainingLocal;
      });

      if (!isSearchMode) {
        reshuffle([...mergedLibrary, ...uploadThingItems], remainingLocal, settings.filter);
      }

      if (stableUserId) {
        const existingMetadata = await storageService.getMediaMetadata(stableUserId);
        const filteredMetadata = existingMetadata.filter((entry) => !syncedLocalIds.includes(entry.id));
        await storageService.saveMediaMetadata(filteredMetadata);
      }
    }

    setUnsyncedSessionLocalIds((prev) => prev.filter((id) => !syncedLocalIds.includes(id)));
    if (failed > 0 && syncedDriveItems.length === 0) {
      setHasNewLocalUploadsThisSession(true);
    }
  }, [authSession, cloudBackupEnabled, isSearchMode, planData?.features.cloudBackup, reshuffle, settings.filter, uploadThingItems]);

  // â”€â”€â”€ Import â”€â”€â”€
  const importFromPicker = async (presetFileIds?: string[]) => {
    if (!authSession) return;
    try {
      addSentryBreadcrumb('picker_open');
      const selection = await openDrivePicker({ accessToken: authSession.accessToken, presetFileIds });
      const selectedFiles = [...selection.files];
      if (selection.folderIds.length > 0) {
        toast.loading('Importing from folder(s)...', { id: 'folder-import' });
        const failedFolderIds: string[] = [];
        let skippedDueToPermissions = 0;
        let reachedPlanLimitWhileScanning = false;
        let remaining = Math.max(0, driveItemsLimit - driveItemsUsed - selectedFiles.length);
        for (const fId of selection.folderIds) {
          try {
            if (remaining <= 0) {
              reachedPlanLimitWhileScanning = true;
              break;
            }
            const scan = await listFolderMediaFilesDetailed(authSession.accessToken, fId, {
              maxItems: remaining,
            });
            skippedDueToPermissions += scan.skippedDueToPermissions;
            reachedPlanLimitWhileScanning = reachedPlanLimitWhileScanning || scan.stoppedAtLimit;
            const files = scan.files;
            selectedFiles.push(...files.map(f => ({ fileId: f.id, name: f.name, mimeType: f.mimeType, thumbnailLink: f.thumbnailLink })));
            remaining = Math.max(0, driveItemsLimit - driveItemsUsed - selectedFiles.length);
          } catch (folderErr) {
            console.warn('Folder import failed for folder:', fId, folderErr);
            failedFolderIds.push(fId);
          }
        }
        if (failedFolderIds.length > 0) {
          toast.info(`Imported available items. Skipped ${failedFolderIds.length} folder(s) due to access/runtime errors.`, {
            id: 'folder-import',
            duration: 5000,
          });
        }
        if (skippedDueToPermissions > 0) {
          toast.info(`Skipped ${skippedDueToPermissions} folder segment(s) due to permissions.`, { id: 'folder-import', duration: 4500 });
        }
        if (reachedPlanLimitWhileScanning) {
          toast.info(`Stopped at ${driveItemsLimit} items; plan limit reached.`, { id: 'folder-import', duration: 4500 });
        }
      }
      const dedup = new Map(selectedFiles.map(i => [i.fileId, i]));
      const final = Array.from(dedup.values());
      // User cancelled or no files selected - this is fine, just return silently
      if (final.length === 0) {
        if (selection.folderIds.length > 0) {
          toast.info(getDriveFileFolderImportEmptyMessage(selection.folderIds.length), { id: 'folder-import', duration: 7000 });
        } else {
          toast.dismiss('folder-import');
        }
        return;
      }

      const remaining = driveItemsLimit - driveItemsUsed;
      if (remaining <= 0) {
        toast.error('Library is full. Upgrade to add more.', { id: 'folder-import', duration: 6000 });
        setUpgradeReason('Your Drive library is full.'); setTargetUpgradeTier('pro'); setShowUpgradePrompt(true); return;
      }

      const upsert: UpsertResponse = await libraryService.upsertLibrary(authSession, final.map(i => ({ fileId: i.fileId, name: i.name, mimeType: i.mimeType, thumbnailLink: i.thumbnailLink })));
      const serverItems: MediaItem[] = upsert.items.map(mapAuthorizedItemToMediaItem);
      const summary = summarizeImportOutcome({
        selectedCount: final.length,
        added: upsert.added,
        skipped: upsert.skipped,
        limitExceeded: upsert.limitExceeded,
      });

      // MERGE with existing library items instead of replacing.
      // This prevents data loss if the server response is incomplete
      // (e.g. due to storage consistency issues between requests).
      const mergedItems: MediaItem[] = (() => {
        const map = new Map(libraryItems.map(i => [i.id, i]));
        for (const item of serverItems) {
          map.set(item.id, item);
        }
        return Array.from(map.values());
      })();

      if (upsert.limitExceeded) {
        toast.info(upsert.message || `${upsert.added} added, ${upsert.skipped} skipped.`, { id: 'folder-import', duration: 6000 });
      } else if (summary.allDuplicates) {
        toast.info(`All ${summary.duplicateCount} selected file${summary.duplicateCount > 1 ? 's are' : ' is'} already in your library.`, {
          id: 'folder-import',
          duration: 4500,
        });
      } else if (summary.mixedAddedAndDuplicates) {
        toast.success(`${upsert.added} added, ${summary.duplicateCount} already in library.`, { id: 'folder-import', duration: 4500 });
      } else if (selection.folderIds.length > 0) {
        toast.success(`Import complete: ${upsert.added} items added.`, { id: 'folder-import', duration: 4000 });
      } else if (upsert.added > 0) {
        toast.success(`${upsert.added} item${upsert.added > 1 ? 's' : ''} added to library.`, { id: 'folder-import', duration: 3000 });
      }

      setLibraryItems(mergedItems);
      reshuffle([...mergedItems, ...uploadThingItems], localItems, settings.filter);
      const effectiveUserId = userId || authSession.userSub || `user-${btoa(authSession.accessToken).substring(0, 16)}`;
      await cacheDriveMetadata(mergedItems, effectiveUserId);
      if (authSession) await refreshPlan(authSession, 'import-picker');
      syncItemsToUploadThing(serverItems, 'drive-import').catch(() => {});
    } catch (err: any) {
      captureSentryException(err, { stage: 'import_from_picker' });
      toast.error(err?.message || 'Import failed.', { id: 'folder-import', duration: 6000 });
    }
  };

  const importFromDropbox = async () => {
    if (!authSession) return;
    if (!isDropboxConfigured()) {
      toast.error('Dropbox is not configured. Set VITE_DROPBOX_APP_KEY.', { duration: 5000 });
      return;
    }

    try {
      let connected = dropboxConnected;
      if (!connected) {
        toast.loading('Connecting Dropbox...', { id: 'dropbox-connect' });
        const status = await connectDropbox(authSession);
        connected = Boolean(status.connected);
        setDropboxConnected(connected);
        setDropboxCursorPath(status.cursorPath || '');
        toast.success('Dropbox connected.', { id: 'dropbox-connect', duration: 2500 });
      }
      if (!connected) {
        throw new Error('Unable to connect Dropbox account.');
      }
      setDropboxImportError(null);
      setDropboxImportPath(dropboxCursorPath || '/');
      setDropboxImportRecursive(true);
      setShowDropboxImportModal(true);
    } catch (error: any) {
      captureSentryException(error, { stage: 'import_from_dropbox' });
      setDropboxConnected(false);
      toast.error(error?.message || 'Dropbox import failed.', { id: 'dropbox-import', duration: 6000 });
    } finally {
      toast.dismiss('dropbox-connect');
    }
  };

  const confirmDropboxImport = useCallback(async (folderInput: string, recursive: boolean) => {
    if (!authSession) return;
    setDropboxImportLoading(true);
    setDropboxImportError(null);
    const folderPath = normalizeDropboxPath(folderInput);
    try {
      toast.loading('Importing Dropbox folder...', { id: 'dropbox-import' });
      const imported = await importDropboxFolder(authSession, folderPath, recursive);
      if (imported.items.length === 0) {
        toast.info('No photos/videos found in that Dropbox folder.', { id: 'dropbox-import', duration: 4000 });
        return;
      }

      const upsert = await libraryService.upsertLibrary(authSession, imported.items);
      const serverItems = upsert.items.map(mapAuthorizedItemToMediaItem);
      const mergedItems: MediaItem[] = (() => {
        const map = new Map(libraryItems.map((item) => [item.id, item]));
        for (const item of serverItems) {
          map.set(item.id, item);
        }
        return Array.from(map.values());
      })();

      setDropboxCursorPath(imported.folderPath || folderPath);
      setLibraryItems(mergedItems);
      reshuffle([...mergedItems, ...uploadThingItems], localItems, settings.filter);
      const effectiveUserId = userId || authSession.userSub || `user-${btoa(authSession.accessToken).substring(0, 16)}`;
      await cacheDriveMetadata(mergedItems, effectiveUserId);
      await refreshPlan(authSession, 'import-dropbox');
      syncItemsToUploadThing(serverItems, 'dropbox-import').catch(() => {});
      setShowDropboxImportModal(false);
      toast.success(`Added ${imported.items.length} Dropbox item${imported.items.length > 1 ? 's' : ''}.`, { id: 'dropbox-import', duration: 3000 });
    } catch (error: any) {
      captureSentryException(error, { stage: 'confirm_dropbox_import' });
      setDropboxImportError(error?.message || 'Dropbox import failed.');
      toast.error(error?.message || 'Dropbox import failed.', { id: 'dropbox-import', duration: 6000 });
    } finally {
      setDropboxImportLoading(false);
    }
  }, [authSession, libraryItems, localItems, settings.filter, userId, reshuffle, cacheDriveMetadata, refreshPlan]);

  const syncDropboxChanges = async () => {
    if (!authSession || !dropboxConnected) return;
    try {
      toast.loading('Syncing Dropbox changes...', { id: 'dropbox-sync' });
      const delta = await syncDropbox(authSession);
      const upsertCandidates = [...delta.newItems, ...delta.updatedItems];

      if (upsertCandidates.length > 0) {
        await libraryService.upsertLibrary(authSession, upsertCandidates);
      }

      if (delta.deletedIds.length > 0) {
        const idsToRemove = libraryItems
          .filter((item) => {
            if (item.source !== 'dropbox') return false;
            if (delta.deletedIds.includes(item.id)) return true;
            const path = item.dropboxFile?.directLink || '';
            return path ? delta.deletedIds.includes(path) : false;
          })
          .map((item) => item.id);
        if (idsToRemove.length > 0) {
          await libraryService.removeFromLibrary(authSession, idsToRemove);
        }
      }

      const effectiveUserId = userId || authSession.userSub || `user-${btoa(authSession.accessToken).substring(0, 16)}`;
      await hydrateFromServerLibrary(authSession, effectiveUserId, 'dropbox-sync');
      if (upsertCandidates.length > 0) {
        const syncCandidates = upsertCandidates.map(mapAuthorizedItemToMediaItem);
        syncItemsToUploadThing(syncCandidates, 'dropbox-sync').catch(() => {});
      }
      toast.success('Dropbox sync complete.', { id: 'dropbox-sync', duration: 3000 });
    } catch (error: any) {
      captureSentryException(error, { stage: 'sync_dropbox' });
      toast.error(error?.message || 'Dropbox sync failed.', { id: 'dropbox-sync', duration: 5000 });
    }
  };

  const createCollection = useCallback(async (name: string) => {
    if (!authSession) return;
    const created = await collectionsService.create(authSession, name);
    setCollections((prev) => [...prev, created]);
  }, [authSession]);

  const renameCollection = useCallback(async (id: string, name: string) => {
    if (!authSession) return;
    const updated = await collectionsService.rename(authSession, id, name);
    setCollections((prev) => prev.map((col) => (col.id === id ? updated : col)));
  }, [authSession]);

  const deleteCollection = useCallback(async (id: string) => {
    if (!authSession) return;
    await collectionsService.remove(authSession, id);
    setCollections((prev) => prev.filter((col) => col.id !== id));
    if (selectedCollectionId === id) {
      setSelectedCollectionId(null);
    }
    const effectiveUserId = userId || authSession.userSub || `user-${btoa(authSession.accessToken).substring(0, 16)}`;
    await hydrateFromServerLibrary(authSession, effectiveUserId, 'delete-collection');
  }, [authSession, hydrateFromServerLibrary, selectedCollectionId, userId]);

  const toggleCurrentItemCollection = useCallback(async (collectionId: string, shouldAdd: boolean) => {
    if (!authSession || !currentItem) return;
    await collectionsService.updateItems(authSession, collectionId, [currentItem.id], shouldAdd ? 'add' : 'remove');
    const effectiveUserId = userId || authSession.userSub || `user-${btoa(authSession.accessToken).substring(0, 16)}`;
    await hydrateFromServerLibrary(authSession, effectiveUserId, 'toggle-item-collection');
    await loadCollections(authSession, 'toggle-item-collection');
  }, [authSession, currentItem, hydrateFromServerLibrary, loadCollections, userId]);

  const handleBulkImport = async (text?: string) => {
    const t = (text ?? '').trim();
    if (!t) { setShowPasteLinks(true); return; }
    const parsed = parseDriveLinksAndIds(t);
    const intent = resolveBulkImportIntent(parsed);
    if (intent.type === 'invalid') {
      toast.info('No valid Drive file or folder links found.');
      return;
    }
    if (!intent.presetFileIds && intent.detectedFolderLinks) {
      toast.info('Folder links detected. Continue in Picker and select the folder to import media.');
      await importFromPicker();
      return;
    }
    await importFromPicker(intent.presetFileIds);
  };

  const handleLocalFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    // Capture files and immediately reset the input so the same files can be re-selected
    const files = Array.from(event.target.files);
    event.target.value = '';

    // Derive userId if not yet in state (mobile timing issue)
    let effectiveUserId = userId;
    if (!effectiveUserId && authSession) {
      effectiveUserId = authSession.userSub || `user-${btoa(authSession.accessToken).substring(0, 16)}`;
      setUserId(effectiveUserId);
    }
    if (!effectiveUserId) {
      toast.error('Please sign in before adding local files.', { duration: 4000 });
      return;
    }

    const requiredStoresReady = await storageService.assertStoresExist([STORES.LOCAL_FILES, STORES.METADATA]);
    if (!requiredStoresReady) {
      toast.error('Local storage is still upgrading. Please wait a few seconds and try again.', { duration: 5000 });
      return;
    }

    const metadataMap = new Map(
      (await storageService.getMediaMetadata(effectiveUserId)).map((meta) => [meta.id, meta])
    );
    const existingContentKeys = new Set<string>();
    for (const meta of metadataMap.values()) {
      if (meta.localFileHash && typeof meta.fileSize === 'number') {
        existingContentKeys.add(buildLocalContentKey(meta.localFileHash, meta.fileSize));
      }
    }
    for (const item of libraryItems) {
      if (item.contentHash && typeof item.sizeBytes === 'number') {
        existingContentKeys.add(buildLocalContentKey(item.contentHash, item.sizeBytes));
      }
    }

    const newItems: MediaItem[] = [];
    let duplicateCount = 0;
    for (const file of files as File[]) {
      try {
        // Calculate hash with fallback for mobile (large files can fail with SHA-256)
        let hash: string;
        try {
          hash = await storageService.calculateFileHash(file);
        } catch (hashErr) {
          console.warn('SHA-256 hash failed, using metadata fallback:', hashErr);
          hash = `${file.name}-${file.size}-${file.lastModified}`;
        }

        const contentKey = buildLocalContentKey(hash, file.size);
        if (existingContentKeys.has(contentKey)) {
          duplicateCount += 1;
          continue;
        }

        const existingFile = await storageService.getLocalFile(hash);
        if (existingFile) {
          duplicateCount += 1;
          continue;
        }
        await storageService.saveLocalFile(hash, file, effectiveUserId);

        // Infer mimeType from extension if file.type is empty (common on mobile Safari)
        let mimeType = file.type;
        if (!mimeType) {
          const ext = file.name.toLowerCase().split('.').pop() || '';
          const MIME_MAP: Record<string, string> = {
            jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
            gif: 'image/gif', webp: 'image/webp', heic: 'image/heic',
            heif: 'image/heif', bmp: 'image/bmp', tiff: 'image/tiff',
            tif: 'image/tiff', svg: 'image/svg+xml',
            mp4: 'video/mp4', mov: 'video/quicktime', avi: 'video/x-msvideo',
            webm: 'video/webm', mkv: 'video/x-matroska', m4v: 'video/x-m4v',
            '3gp': 'video/3gpp',
          };
          mimeType = MIME_MAP[ext] || 'application/octet-stream';
        }

        const itemId = `local-${hash.substring(0, 16)}`;
        existingContentKeys.add(contentKey);
        newItems.push({
          id: itemId,
          name: file.name,
          mimeType,
          source: 'local',
          file,
          contentHash: hash,
          sizeBytes: file.size,
          originalSource: 'local',
          syncStatus: 'device-only',
        });
        metadataMap.set(itemId, {
          id: itemId, userId: effectiveUserId, localFileHash: hash, name: file.name,
          mimeType, fileSize: file.size, source: 'local' as const,
          indexedAt: new Date().toISOString(),
        });
      } catch (err: any) {
        console.error('Error processing local file:', file.name, err);
        toast.error(`Failed to add "${file.name}": ${err?.message || 'Unknown error'}`, { duration: 4000 });
      }
    }
    if (newItems.length > 0) {
      await storageService.saveMediaMetadata(Array.from(metadataMap.values()));
      const updated = [...localItems, ...newItems]; setLocalItems(updated);
      setUnsyncedSessionLocalIds((prev) => Array.from(new Set([...prev, ...newItems.map((item) => item.id)])));
      setHasNewLocalUploadsThisSession(true);
      if (!isSearchMode) reshuffle([...libraryItems, ...uploadThingItems], updated, settings.filter);
      if (cloudBackupEnabled && planData?.features.cloudBackup && authSession) {
        syncLocalItemsToDrive(newItems, effectiveUserId).catch((error) => {
          console.warn('Drive sync upload failed', error);
        });
        syncItemsToUploadThing(newItems, 'local-upload').catch(() => {});
      }
      toast.success(`Added ${newItems.length} local file${newItems.length > 1 ? 's' : ''}.`, { duration: 3000 });
    }
    if (duplicateCount > 0) {
      toast.info('Already in your library.', { duration: 2800 });
    }
  };

  const retryCurrentLocalSync = async () => {
    if (!currentItem || currentItem.source !== 'local' || !userId) return;
    if (!cloudBackupEnabled || !planData?.features.cloudBackup) {
      toast.error('Enable Cloud Storage on a Pro plan to sync this local file across devices.');
      return;
    }
    await syncLocalItemsToDrive([currentItem], userId);
  };

  // â”€â”€â”€ Search â”€â”€â”€
  const handleSearch = async (query: string) => {
    if (!userId) return;
    setIsSearching(true);
    try {
      const cached = await searchCacheService.getCachedSearch(userId, query);
      const allItems = [...libraryItems, ...uploadThingItems, ...localItems];
      if (cached) {
        setActiveSearchQuery(query); const cachedItems = allItems.filter(i => cached.resultIds.includes(i.id));
        setSearchResults(cachedItems); setIsSearchMode(true); setShowSearch(false);
        reshuffle(cachedItems.filter(i => i.source !== 'local'), cachedItems.filter(i => i.source === 'local'), settings.filter); return;
      }
      if (!aiEnabled) {
        const kw = query.toLowerCase().split(/\s+/).filter(Boolean);
        setActiveSearchQuery(query);
        const results = allItems.filter(i => kw.some(k => i.name.toLowerCase().includes(k)));
        setSearchResults(results); setIsSearchMode(true); setShowSearch(false);
        reshuffle(results.filter(i => i.source !== 'local'), results.filter(i => i.source === 'local'), settings.filter);
        toast.info('AI search is a Pro feature. Using keyword search.', { duration: 4000 }); return;
      }
      const canSearch = await usageService.canPerformAction(userId, 'ai_search');
      if (!canSearch.allowed) { setUpgradeReason(canSearch.reason || 'AI search limit reached.'); setTargetUpgradeTier('pro'); setShowUpgradePrompt(true); setIsSearching(false); return; }
      const cachedTags = await searchCacheService.getCachedTags(query);
      let tags = cachedTags || await generateSearchTags(query, authSession, true);
      if (!cachedTags) await usageService.trackUsage(userId, 'ai_search');
      setActiveSearchQuery(query);
      const lowered = tags.map(t => t.toLowerCase());
      const results = allItems.filter(i => lowered.some(tag => i.name.toLowerCase().includes(tag)));
      await searchCacheService.saveCachedSearch(userId, query, tags, results.map(i => i.id));
      setSearchResults(results); setIsSearchMode(true); setShowSearch(false);
      reshuffle(results.filter(i => i.source !== 'local'), results.filter(i => i.source === 'local'), settings.filter);
    } catch (err: any) { alert('Search failed: ' + err.message); }
    finally { setIsSearching(false); }
  };

  const clearSearch = () => { setIsSearchMode(false); setSearchResults([]); setActiveSearchQuery(''); reshuffle([...libraryItems, ...uploadThingItems], localItems, settings.filter); };

  // â”€â”€â”€ AI Analysis â”€â”€â”€
  const handleAnalyze = async () => {
    if (!currentItem) return;
    setIsAnalyzing(true); setAnalysis(null);
    const result = await analyzeImage(currentItem, authSession);
    setAnalysis(result); setIsAnalyzing(false);
  };

  // â”€â”€â”€ Navigation â”€â”€â”€
  const nextSlide = useCallback(() => { setCurrentIndex(p => (p + 1) % shuffledQueue.length); }, [shuffledQueue.length]);
  const prevSlide = useCallback(() => { setCurrentIndex(p => (p - 1 + shuffledQueue.length) % shuffledQueue.length); }, [shuffledQueue.length]);

  const startSlideShow = useCallback(() => {
    if (slideshowTimerRef.current) { clearTimeout(slideshowTimerRef.current); slideshowTimerRef.current = null; }
    if (!isMediaLoaded) return;
    slideshowTimerRef.current = window.setTimeout(() => {
      setCurrentIndex(p => { const q = shuffledQueueRef.current; return q.length === 0 ? p : (p + 1) % q.length; });
    }, settings.interval * 1000);
  }, [settings.interval, isMediaLoaded]);

  const stopSlideShow = () => { if (slideshowTimerRef.current) { clearTimeout(slideshowTimerRef.current); slideshowTimerRef.current = null; } };

  useEffect(() => {
    const cur = shuffledQueue[currentIndex];
    const isVid = cur ? getMediaType(cur) === 'video' : false;
    if (!settings.isPlaying || isVid || !isMediaLoaded) { stopSlideShow(); return; }
    startSlideShow();
    return () => stopSlideShow();
  }, [settings.isPlaying, currentIndex, startSlideShow, shuffledQueue, isMediaLoaded]);

  // â”€â”€â”€ Progress bar for slideshow â”€â”€â”€
  // Reset start time when slide changes
  useEffect(() => {
    if (settings.isPlaying && isMediaLoaded) {
      progressStartRef.current = Date.now();
      setProgress(0);
    }
  }, [currentIndex, settings.isPlaying, isMediaLoaded]);

  // Set up interval for progress updates
  useEffect(() => {
    if (settings.isPlaying && isMediaLoaded && progressStartRef.current !== null) {
      progressRef.current = window.setInterval(() => {
        if (progressStartRef.current !== null) {
          setProgress(Math.min((Date.now() - progressStartRef.current) / (settings.interval * 1000), 1));
        }
      }, 30);
    } else {
      setProgress(0);
      progressStartRef.current = null;
    }
    return () => {
      if (progressRef.current) {
        clearInterval(progressRef.current);
        progressRef.current = null;
      }
    };
  }, [settings.isPlaying, isMediaLoaded, settings.interval]);

  // â”€â”€â”€ Auto-hide controls â”€â”€â”€
  const resetHideTimer = useCallback(() => {
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => { if (settings.isPlaying) setControlsVisible(false); }, 3500);
  }, [settings.isPlaying]);
  useEffect(() => { resetHideTimer(); return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); }; }, [resetHideTimer, currentIndex]);

  const toggleLike = (id: string) => { setLiked(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
    const onChange = () => setIsMobile(mediaQuery.matches);
    onChange();
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => setIsNativeFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreenChange);
    onFullscreenChange();
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    try {
      const pref = sessionStorage.getItem(SESSION_FULLSCREEN_PREF_KEY);
      if (pref === 'pseudo') setIsPseudoFullscreen(true);
    } catch {}
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_FULLSCREEN_PREF_KEY, isPseudoFullscreen ? 'pseudo' : 'off');
    } catch {}
  }, [isPseudoFullscreen]);

  useEffect(() => {
    const lockBody = isPseudoFullscreen || showMobileMenu;
    if (!lockBody) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isPseudoFullscreen, showMobileMenu]);

  useEffect(() => {
    if (!showMobileMenu) return;
    const sheet = mobileMenuRef.current;
    const focusable = sheet?.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const first = focusable?.[0];
    const last = focusable?.[focusable.length - 1];
    first?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowMobileMenu(false);
        return;
      }
      if (event.key !== 'Tab' || !first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showMobileMenu]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (showDesktopMoreMenu && desktopMoreMenuRef.current && !desktopMoreMenuRef.current.contains(target)) {
        setShowDesktopMoreMenu(false);
      }
      if (showSourceMenu && sourceMenuRef.current && !sourceMenuRef.current.contains(target)) {
        setShowSourceMenu(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (showDesktopMoreMenu) setShowDesktopMoreMenu(false);
      if (showSourceMenu) setShowSourceMenu(false);
      if (showDropboxImportModal && !dropboxImportLoading) setShowDropboxImportModal(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showDesktopMoreMenu, showSourceMenu, showDropboxImportModal, dropboxImportLoading]);

  // â”€â”€â”€ Progress ring â”€â”€â”€
  const circumference = 2 * Math.PI * 26;
  const strokeDashoffset = circumference - progress * circumference;

  const isPhotoItem = currentItem ? isPhoto(currentItem) : false;

  // â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
  //  RENDER
  // â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

  // Loading
  if (loading) return (
    <div className="w-screen h-screen flex items-center justify-center" style={{ background: '#0C0A14' }}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300&family=Outfit:wght@200;300;400;500;600&display=swap" rel="stylesheet" />
      <div className="text-center">
        <Loader2 className="w-10 h-10 text-white/20 animate-spin mx-auto mb-4" />
        <p className="text-white/20 text-sm" style={{ fontFamily: SANS, fontWeight: 200 }}>{loadStatusMessage || 'Loading...'}</p>
      </div>
    </div>
  );

  // Not authenticated - Welcome
  if (!isAuthenticated) return (
    <div className="w-screen h-screen flex items-center justify-center overflow-hidden" style={{ background: '#0C0A14' }}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400&family=Outfit:wght@200;300;400;500;600&display=swap" rel="stylesheet" />
      <style>{GLASS_STYLE}</style>
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute w-[500px] h-[500px] rounded-full opacity-15" style={{ background: 'radial-gradient(circle, #C4B5FD40, transparent 70%)', top: '10%', right: '-10%', animation: 'float1 20s ease-in-out infinite' }} />
        <div className="absolute w-[400px] h-[400px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #FDA4AF30, transparent 70%)', bottom: '5%', left: '-5%', animation: 'float2 25s ease-in-out infinite' }} />
      </div>
      <div className="relative z-10 text-center max-w-md px-8" style={{ animation: 'fadeScale 0.8s ease-out' }}>
        <h1 className="text-5xl md:text-6xl font-light text-white mb-4 tracking-tight" style={{ fontFamily: SERIF }}>Refract</h1>
        <p className="text-white/25 text-sm mb-10 leading-relaxed" style={{ fontFamily: SANS, fontWeight: 200 }}>
          Rediscover your memories. Files are stored locally in your browser by default. Optional cloud backup is available for cross-device access.
        </p>
        {error && <p className="text-rose-400/70 text-sm mb-4" style={{ fontFamily: SANS }}>{error}</p>}
        <button onClick={handleLogin}
          className="px-10 py-4 rounded-2xl text-sm font-medium transition-all duration-300 hover:scale-[1.03] active:scale-[.97]"
          style={{ background: 'linear-gradient(135deg, #C4B5FD, #C4B5FD80)', color: '#0C0A14', fontFamily: SANS, boxShadow: '0 8px 40px #C4B5FD30' }}>
          Sign in with Google
        </button>
        <p className="text-white/10 text-xs mt-6" style={{ fontFamily: SANS, fontWeight: 200 }}>Your photos and videos never touch our servers</p>
        <div className="mt-4 flex items-center justify-center gap-4 text-xs text-white/30" style={{ fontFamily: SANS }}>
          <a href="/privacy" className="hover:text-white/60">Privacy Policy</a>
          <a href="/terms" className="hover:text-white/60">Terms of Service</a>
        </div>
      </div>
    </div>
  );

  // Empty library
  if (shuffledQueue.length === 0 && !isSearchMode) return (
    <div className="w-screen h-screen flex flex-col items-center justify-center overflow-hidden" style={{ background: '#0C0A14' }}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400&family=Outfit:wght@200;300;400;500;600&display=swap" rel="stylesheet" />
      <style>{GLASS_STYLE}</style>
      <input type="file" ref={fileInputRef} onChange={handleLocalFileSelect} className="hidden" multiple accept="image/*,video/*" />
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute w-[500px] h-[500px] rounded-full opacity-15" style={{ background: 'radial-gradient(circle, #C4B5FD40, transparent 70%)', top: '10%', right: '-10%', animation: 'float1 20s ease-in-out infinite' }} />
      </div>
      <div className="relative z-10 text-center max-w-lg px-8" style={{ animation: 'fadeScale 0.6s ease-out' }}>
        <Plus className="w-12 h-12 text-white/15 mx-auto mb-6" />
        <h2 className="text-3xl font-light text-white mb-2" style={{ fontFamily: SERIF }}>No Media Yet</h2>
        <p className="text-white/20 text-sm mb-6" style={{ fontFamily: SANS, fontWeight: 200 }}>Add photos and videos to start your journey</p>
        <div className="mb-6 flex justify-center"><GlassUsageBar used={driveItemsUsed} limit={driveItemsLimit} tier={currentTier} aiEnabled={aiEnabled} onUpgradeClick={handleUpgradeClick} accent="#C4B5FD" loading={usageLoading} /></div>
        {cloudBackupUnavailableMessage && (
          <div className="mb-4 rounded-xl border border-amber-400/35 bg-amber-500/10 px-3 py-2 text-xs text-amber-100" style={{ fontFamily: SANS }}>
            {cloudBackupUnavailableMessage}
          </div>
        )}
        <div className="flex flex-wrap justify-center gap-3 mb-6">
          <button onClick={() => fileInputRef.current?.click()} className="px-6 py-3 rounded-2xl glass-panel text-white/50 hover:text-white text-sm transition-all hover:scale-[1.02] flex items-center gap-2" style={{ fontFamily: SANS }}><Plus size={16} />Add Local</button>
          <button onClick={() => importFromPicker()} className="px-6 py-3 rounded-2xl text-sm font-medium transition-all hover:scale-[1.02]" style={{ background: 'linear-gradient(135deg, #C4B5FD, #C4B5FD80)', color: '#0C0A14', fontFamily: SANS }}>Add from Drive</button>
          <button onClick={importFromDropbox} className="px-6 py-3 rounded-2xl glass-panel text-white/50 hover:text-white text-sm transition-all hover:scale-[1.02]">Add from Dropbox</button>
          {dropboxConnected && (
            <button onClick={syncDropboxChanges} className="px-6 py-3 rounded-2xl glass-panel text-blue-300/70 hover:text-blue-200 text-sm transition-all hover:scale-[1.02]">Sync Dropbox</button>
          )}
          <button onClick={() => setShowPasteLinks(true)} className="px-6 py-3 rounded-2xl glass-panel text-white/50 hover:text-white text-sm transition-all hover:scale-[1.02] flex items-center gap-2" style={{ fontFamily: SANS }}><Link2 size={16} />Paste Links</button>
          <button onClick={() => setShowCollections(true)} className="px-6 py-3 rounded-2xl glass-panel text-white/50 hover:text-white text-sm transition-all hover:scale-[1.02] flex items-center gap-2" style={{ fontFamily: SANS }}><FolderHeart size={16} />Collections</button>
        </div>
        <div className="flex justify-center gap-3">
          <button onClick={() => setShowSettings(true)} className="px-5 py-2.5 rounded-xl glass-panel text-white/30 hover:text-white/60 text-xs transition-colors" style={{ fontFamily: SANS }}><Settings size={14} className="inline mr-1" />Settings</button>
          <button onClick={handleLogout} className="px-5 py-2.5 rounded-xl glass-panel text-rose-400/30 hover:text-rose-400/70 text-xs transition-colors" style={{ fontFamily: SANS }}><LogOut size={14} className="inline mr-1" />Sign Out</button>
        </div>
      </div>
      {showSettings && (
        <GlassSettingsModal
          settings={settings}
          cloudBackupEnabled={cloudBackupEnabled}
          isCloudBackupAvailable={Boolean(planData?.features.cloudBackup)}
          onToggleCloudBackup={handleCloudBackupToggle}
          dropboxConnected={dropboxConnected}
          onConnectDropbox={importFromDropbox}
          onSyncDropbox={syncDropboxChanges}
          onDisconnectDropbox={async () => {
            if (!authSession) return;
            try {
              await disconnectDropbox(authSession);
              setDropboxConnected(false);
              setDropboxCursorPath('');
              toast.success('Dropbox disconnected.', { duration: 2500 });
            } catch (error: any) {
              toast.error(error?.message || 'Failed to disconnect Dropbox.');
            }
          }}
          onClose={() => setShowSettings(false)}
          onUpdate={s => setSettings(p => ({ ...p, ...s }))}
          onDeleteAccount={handleDeleteAccount}
          accent="#C4B5FD"
        />
      )}
      <GlassPasteLinksModal isOpen={showPasteLinks} onClose={() => setShowPasteLinks(false)} onSubmit={async v => { setShowPasteLinks(false); await handleBulkImport(v); }} accent="#C4B5FD" />
      {userId && <GlassSubscriptionModal isOpen={showSubscription} onClose={() => setShowSubscription(false)} userId={userId} session={authSession} onPlanChange={p => { setPlanData(p); setSubscriptionTier(p.tier); }} accent="#C4B5FD" indexedItemsUsed={driveItemsUsed} usageLoading={usageLoading} />}
      {/* Must stay mounted here as well as in the main player view: the usage
          bar's Upgrade button and CollectionsModal's onUpgrade both call
          handleUpgradeClick from this branch, and without this the click only
          flipped showUpgradePrompt with nothing rendered to react to it. */}
      <GlassUpgradePrompt isOpen={showUpgradePrompt} onClose={() => setShowUpgradePrompt(false)} reason={upgradeReason} currentTier={subscriptionTier} targetTier={targetUpgradeTier} userId={userId} accent="#C4B5FD" />
      <GlassDropboxImportModal
        isOpen={showDropboxImportModal}
        initialPath={dropboxImportPath}
        initialRecursive={dropboxImportRecursive}
        loading={dropboxImportLoading}
        error={dropboxImportError}
        onCancel={() => {
          if (dropboxImportLoading) return;
          setShowDropboxImportModal(false);
          setDropboxImportError(null);
        }}
        onImport={async (path, recursive) => {
          setDropboxImportPath(path);
          setDropboxImportRecursive(recursive);
          await confirmDropboxImport(path, recursive);
        }}
      />
      <CollectionsModal
        isOpen={showCollections}
        collections={collections}
        selectedCollectionId={selectedCollectionId}
        currentItem={currentItem}
        enabled={collectionsEnabled}
        limit={collectionsLimit}
        onClose={() => setShowCollections(false)}
        onCreate={createCollection}
        onRename={renameCollection}
        onDelete={deleteCollection}
        onToggleCurrentItem={toggleCurrentItemCollection}
        onSelectCollection={setSelectedCollectionId}
        onUpgrade={handleUpgradeClick}
      />
      <SignOutConfirmModal
        isOpen={showSignOutModal}
        cloudBackupEnabled={cloudBackupEnabled}
        onCancel={() => setShowSignOutModal(false)}
        onSignOutOnly={async () => {
          setShowSignOutModal(false);
          await performLogout(false);
        }}
        onExportAndSignOut={async () => {
          setShowSignOutModal(false);
          await performLogout(true);
        }}
      />
      <Toaster position="top-center" richColors />
    </div>
  );

  // â•â•â• MAIN PLAYER VIEW â•â•â•
  return (
    <div className="relative w-screen h-[100dvh] overflow-hidden select-none" onMouseMove={resetHideTimer}
      style={{ fontFamily: SANS, background: '#0C0A14' }}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400&family=Outfit:wght@200;300;400;500;600&display=swap" rel="stylesheet" />
      <style>{GLASS_STYLE}</style>
      <input type="file" ref={fileInputRef} onChange={handleLocalFileSelect} className="hidden" multiple accept="image/*,video/*" />

      {/* â•â•â• BACKGROUND â•â•â• */}
      <div className="absolute inset-0 z-0">
        {currentItem && (
          <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at center, ${accent}12 0%, transparent 60%)`, transition: 'background 2s ease' }} />
        )}
      </div>

      {/* â•â•â• FLOATING BLOBS â•â•â• */}
      <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
        <div className="absolute w-[500px] h-[500px] rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${accent}40, transparent 70%)`, top: '10%', right: '-10%', animation: 'float1 20s ease-in-out infinite', transition: 'background 2s ease' }} />
        <div className="absolute w-[400px] h-[400px] rounded-full opacity-15" style={{ background: 'radial-gradient(circle, rgba(253,164,175,.3), transparent 70%)', bottom: '5%', left: '-5%', animation: 'float2 25s ease-in-out infinite' }} />
        <div className="absolute w-[300px] h-[300px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, rgba(165,243,252,.3), transparent 70%)', top: '50%', left: '40%', animation: 'float3 18s ease-in-out infinite' }} />
      </div>

      {/* â•â•â• MAIN CONTENT â•â•â• */}
      <div className="relative z-10 h-full flex flex-col">

        {/* â”€â”€â”€ TOP NAV â”€â”€â”€ */}
        <div className={`flex items-center justify-between px-6 md:px-8 pt-5 transition-all duration-700 ${controlsVisible && !isPseudoFullscreen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <div className="flex flex-col max-w-[55%]">
            <div className="flex items-center gap-3">
              <span className="text-white/20 text-[10px] tracking-[.25em] uppercase" style={{ fontWeight: 300 }}>Refract</span>
              <span className="text-white/10 text-[10px]">{currentIndex + 1} / {shuffledQueue.length}</span>
            </div>
            <h1 className="text-white text-lg md:text-xl font-light truncate mt-1" style={{ fontFamily: SERIF }}>{currentItem?.name}</h1>
            <div className="flex items-center gap-2 mt-1.5">
              <span className={`text-[9px] px-1.5 py-0.5 rounded-md font-medium ${
                currentItem?.source === 'local'
                  ? 'bg-emerald-500/15 text-emerald-300/60'
                  : currentItem?.source === 'dropbox'
                    ? 'bg-indigo-500/15 text-indigo-300/70'
                    : currentItem?.source === 'uploadthing'
                      ? 'bg-cyan-500/15 text-cyan-300/70'
                    : 'bg-blue-500/15 text-blue-300/60'
              }`}>
                {currentItem?.source === 'local' ? 'LOCAL' : currentItem?.source === 'dropbox' ? 'DROPBOX' : currentItem?.source === 'uploadthing' ? 'PERSISTED' : 'DRIVE'}
              </span>
              <GlassUsageBar used={driveItemsUsed} limit={driveItemsLimit} tier={currentTier} aiEnabled={aiEnabled} onUpgradeClick={handleUpgradeClick} accent={accent} loading={usageLoading} />
            </div>
            {currentItem?.source === 'local' && unsyncedSessionLocalIds.includes(currentItem.id) && (
              <div className="mt-1 flex items-center gap-2">
                <span className="text-[10px] text-amber-300/80">Device-only (not synced)</span>
                <button
                  onClick={retryCurrentLocalSync}
                  className="text-[10px] px-2 py-0.5 rounded-md glass-panel text-blue-300/85 hover:text-blue-200"
                >
                  Retry sync
                </button>
              </div>
            )}
            {isSearchMode && (
              <div className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-lg w-fit" style={{ background: `${accent}15`, color: accent + '90' }}>
                <Search size={10} /> "{activeSearchQuery}"
                {!aiEnabled && <span className="text-yellow-300/50 text-[9px]">(keyword)</span>}
                <button onClick={clearSearch} className="ml-1 hover:opacity-70"><XCircle size={11} /></button>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="hidden md:flex items-center gap-1.5">
            <button onClick={() => setShowSearch(true)} className="p-2.5 rounded-xl glass-panel text-white/30 hover:text-white/70 transition-all hover:scale-105" title="Search"><Search size={16} /></button>
            <button onClick={() => fileInputRef.current?.click()} className="p-2.5 rounded-xl glass-panel text-white/30 hover:text-white/70 transition-all hover:scale-105" title="Add Local"><Plus size={16} /></button>
            <div className="relative" ref={sourceMenuRef}>
              <button
                onClick={() => setShowSourceMenu((prev) => !prev)}
                className="px-3 py-2.5 rounded-xl glass-panel text-white/40 hover:text-white/75 transition-all hover:scale-105 text-[10px]"
                title="Source selector"
                style={{ fontWeight: 500 }}
              >
                Source
              </button>
              {showSourceMenu && (
                <div className="absolute right-0 mt-2 w-44 rounded-xl glass-modal p-2 z-[88] space-y-1">
                  <button className="w-full text-left px-3 py-2 rounded-lg glass-panel text-white/70 hover:text-white text-xs" onClick={() => { setShowSourceMenu(false); importFromPicker(); }}>
                    Add from Drive
                  </button>
                  <button className="w-full text-left px-3 py-2 rounded-lg glass-panel text-white/70 hover:text-white text-xs" onClick={() => { setShowSourceMenu(false); importFromDropbox(); }}>
                    Add from Dropbox
                  </button>
                </div>
              )}
            </div>
            <button onClick={toggleFullscreen} className="p-2.5 rounded-xl glass-panel text-white/30 hover:text-white/70 transition-all hover:scale-105" title="Toggle Fullscreen">
              {isNativeFullscreen || isPseudoFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            {userId && (
              <button onClick={() => setShowSubscription(true)} className="p-2.5 rounded-xl glass-panel transition-all hover:scale-105" title="Plans" style={{ color: '#FBBF2450' }}><Crown size={16} /></button>
            )}
            <div className="relative" ref={desktopMoreMenuRef}>
              <button onClick={() => setShowDesktopMoreMenu((prev) => !prev)} className="p-2.5 rounded-xl glass-panel text-white/35 hover:text-white/75 transition-all hover:scale-105" title="More actions">
                <MoreHorizontal size={16} />
              </button>
              {showDesktopMoreMenu && (
                <div className="absolute right-0 mt-2 w-56 rounded-2xl glass-modal p-2 z-[88] space-y-1">
                  <button className="w-full text-left px-3 py-2 rounded-lg glass-panel text-white/70 hover:text-white text-xs" onClick={() => { setShowDesktopMoreMenu(false); setShowCollections(true); }}>
                    Collections
                  </button>
                  <button className="w-full text-left px-3 py-2 rounded-lg glass-panel text-white/70 hover:text-white text-xs" onClick={() => { setShowDesktopMoreMenu(false); setShowSettings(true); }}>
                    Settings
                  </button>
                  <button className="w-full text-left px-3 py-2 rounded-lg glass-panel text-white/70 hover:text-white text-xs" onClick={async () => { setShowDesktopMoreMenu(false); await exportLocalManifest(); }}>
                    Export manifest
                  </button>
                  <button className="w-full text-left px-3 py-2 rounded-lg glass-panel text-blue-300/80 hover:text-blue-200 text-xs disabled:opacity-40 disabled:cursor-not-allowed" disabled={!dropboxConnected} onClick={() => { setShowDesktopMoreMenu(false); syncDropboxChanges(); }}>
                    DB Sync
                  </button>
                  <button className="w-full text-left px-3 py-2 rounded-lg glass-panel text-white/70 hover:text-white text-xs" onClick={() => { setShowDesktopMoreMenu(false); window.location.href = '/developers'; }}>
                    Developer API
                  </button>
                  <button className="w-full text-left px-3 py-2 rounded-lg glass-panel text-white/70 hover:text-white text-xs" onClick={() => { setShowDesktopMoreMenu(false); window.location.href = '/faq'; }}>
                    FAQ
                  </button>
                  <button className="w-full text-left px-3 py-2 rounded-lg glass-panel text-rose-300/75 hover:text-rose-200 text-xs" onClick={() => { setShowDesktopMoreMenu(false); handleLogout(); }}>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="md:hidden flex items-center gap-2">
            <button onClick={toggleFullscreen} className="p-2.5 rounded-xl glass-panel text-white/35 hover:text-white/70" title="Fullscreen">
              {isNativeFullscreen || isPseudoFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            <button onClick={() => setShowMobileMenu(true)} className="p-2.5 rounded-xl glass-panel text-white/35 hover:text-white/70" title="Menu">
              <Menu size={16} />
            </button>
          </div>
        </div>

        {loadStatusMessage && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-40 px-3 py-1.5 rounded-xl glass-panel text-xs text-white/75">
            {loadStatusMessage}
          </div>
        )}
        {cloudBackupUnavailableMessage && (
          <div className="absolute top-24 left-1/2 -translate-x-1/2 z-40 max-w-[90vw] px-3 py-2 rounded-xl border border-amber-400/35 bg-amber-500/10 text-xs text-amber-100">
            {cloudBackupUnavailableMessage}
          </div>
        )}

        {isPseudoFullscreen && (
          <button
            onClick={() => setIsPseudoFullscreen(false)}
            className="absolute top-4 right-4 z-50 p-2.5 rounded-xl glass-panel text-white/75 hover:text-white"
            title="Exit fullscreen"
          >
            <Minimize2 size={16} />
          </button>
        )}

        {/* â”€â”€â”€ AI Analysis overlay â”€â”€â”€ */}
        <GlassAIOverlay analysis={analysis} isAnalyzing={isAnalyzing} onClose={() => { setAnalysis(null); setIsAnalyzing(false); }} accent={accent} />

        {/* â”€â”€â”€ CENTER PLAYER â”€â”€â”€ */}
        <div className="flex-1 flex items-center justify-center px-4 md:px-8 py-3 min-h-0">
          <div className={`relative w-full h-full ${isPseudoFullscreen ? 'max-w-[100vw] max-h-[100dvh] rounded-none' : 'max-w-[90vw] max-h-[65vh] md:max-w-[85vw] md:max-h-[68vh] rounded-3xl'} overflow-hidden`}
            style={{ animation: 'pulseGlow 4s ease-in-out infinite', boxShadow: `0 25px 80px -20px ${accent}30`, transition: 'box-shadow 2s ease' }}>
            <div className={`absolute inset-0 z-20 pointer-events-none ${isPseudoFullscreen ? 'rounded-none' : 'rounded-3xl'}`} style={{ border: '1px solid rgba(255,255,255,.08)' }} />
            <EtherealPlayer
              item={currentItem} accessToken={accessToken}
              onVideoEnded={() => { if (settings.isPlaying) nextSlide(); }}
              isActive={true}
              onToggleUI={() => setControlsVisible(p => !p)}
              onMediaLoaded={() => setIsMediaLoaded(true)}
            />
          </div>
        </div>

        {/* â”€â”€â”€ BOTTOM CONTROLS â”€â”€â”€ */}
        <div className={`px-4 md:px-8 pb-6 md:pb-8 transition-all duration-700 ${controlsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8 pointer-events-none'}`}>

          {/* Title + actions */}
          <div className="flex items-end justify-between mb-4 md:mb-5">
            <div style={{ animation: controlsVisible ? 'slideInRight 0.6s ease-out' : 'none' }}>
              <h1 className="text-white text-2xl md:text-4xl font-light tracking-tight leading-tight" style={{ fontFamily: SERIF }}>
                {currentItem?.name}
              </h1>
              <p className="text-white/15 text-[10px] tracking-[.15em] uppercase mt-1" style={{ fontWeight: 200 }}>
                {settings.isPlaying ? 'Now playing' : 'Paused'} · {selectedCollectionId ? 'Shuffled selected collection' : 'Shuffled all indexed items'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowCollections(true)} className="p-2.5 rounded-2xl glass-panel transition-all duration-300 hover:scale-105 active:scale-95" title="Add to collection">
                <FolderPlus size={16} className="text-white/25" />
              </button>
              {isPhotoItem && (
                <button onClick={handleAnalyze} disabled={isAnalyzing}
                  className={`p-2.5 rounded-2xl glass-panel transition-all duration-300 hover:scale-105 active:scale-95 ${isAnalyzing ? 'opacity-50' : ''}`}>
                  <Sparkles size={16} style={{ color: accent + '70' }} />
                </button>
              )}
              <button onClick={() => currentItem && toggleLike(currentItem.id)} className="p-2.5 rounded-2xl glass-panel transition-all duration-300 hover:scale-105 active:scale-95">
                <Heart size={16} className={currentItem && liked.has(currentItem.id) ? 'text-rose-400' : 'text-white/25'} fill={currentItem && liked.has(currentItem.id) ? 'currentColor' : 'none'} />
              </button>
              <button className="p-2.5 rounded-2xl glass-panel transition-all duration-300 hover:scale-105 active:scale-95"><Share2 size={16} className="text-white/25" /></button>
              <button onClick={() => setShowSettings(true)} className="p-2.5 rounded-2xl glass-panel transition-all duration-300 hover:scale-105 active:scale-95"><Settings size={16} className="text-white/25" /></button>
            </div>
          </div>

          {/* Transport bar */}
          <div className="glass-panel rounded-3xl px-5 md:px-8 py-4 md:py-5 flex items-center justify-between">
            <div className="flex items-center gap-4 md:gap-5">
              <button onClick={prevSlide} className="text-white/25 hover:text-white/60 transition-all duration-300 active:scale-90"><SkipBack size={18} /></button>

              {/* Play button with progress ring */}
              <div className="relative">
                <svg className="absolute -inset-1 w-[60px] h-[60px]" viewBox="0 0 56 56">
                  <circle cx="28" cy="28" r="26" fill="none" stroke="rgba(255,255,255,.04)" strokeWidth="2" />
                  <circle cx="28" cy="28" r="26" fill="none" stroke={accent} strokeWidth="2" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round" transform="rotate(-90 28 28)" style={{ transition: 'stroke 1s ease, stroke-dashoffset 0.1s linear' }} />
                </svg>
                <button onClick={() => setSettings(p => ({ ...p, isPlaying: !p.isPlaying }))}
                  className="relative w-[52px] h-[52px] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-105 active:scale-95"
                  style={{ background: `linear-gradient(135deg, ${accent}, ${accent}80)`, boxShadow: `0 8px 32px ${accent}40`, transition: 'all 1s ease' }}>
                  {settings.isPlaying ? <Pause size={18} className="text-black/80" fill="currentColor" /> : <Play size={18} className="text-black/80 ml-0.5" fill="currentColor" />}
                </button>
              </div>

              <button onClick={nextSlide} className="text-white/25 hover:text-white/60 transition-all duration-300 active:scale-90"><SkipForward size={18} /></button>
            </div>

            {/* Dot indicators (mobile) */}
            <div className="flex md:hidden items-center gap-1.5">
              {shuffledQueue.slice(Math.max(0, currentIndex - 3), currentIndex + 4).map((item, i) => {
                const realIdx = Math.max(0, currentIndex - 3) + i;
                return (
                  <div key={item.id} className="rounded-full transition-all duration-500"
                    style={{ width: realIdx === currentIndex ? '16px' : '5px', height: '5px', background: realIdx === currentIndex ? accent : 'rgba(255,255,255,.12)' }} />
                );
              })}
            </div>

            {/* Desktop: compact info */}
            <div className="hidden md:flex items-center gap-4 text-white/15 text-[10px]" style={{ fontWeight: 200 }}>
              <span>{settings.interval}s interval</span>
              <span>·</span>
              <span>{settings.filter === MediaType.ALL ? 'All media' : settings.filter === MediaType.PHOTO ? 'Photos only' : 'Videos only'}</span>
              <span>·</span>
              <span>{shuffledQueue.length} items</span>
            </div>
          </div>
        </div>
      </div>

      {/* â•â•â• MODALS â•â•â• */}
      {showSettings && (
        <GlassSettingsModal
          settings={settings}
          cloudBackupEnabled={cloudBackupEnabled}
          isCloudBackupAvailable={Boolean(planData?.features.cloudBackup)}
          onToggleCloudBackup={handleCloudBackupToggle}
          dropboxConnected={dropboxConnected}
          onConnectDropbox={importFromDropbox}
          onSyncDropbox={syncDropboxChanges}
          onDisconnectDropbox={async () => {
            if (!authSession) return;
            try {
              await disconnectDropbox(authSession);
              setDropboxConnected(false);
              setDropboxCursorPath('');
              toast.success('Dropbox disconnected.', { duration: 2500 });
            } catch (error: any) {
              toast.error(error?.message || 'Failed to disconnect Dropbox.');
            }
          }}
          onClose={() => setShowSettings(false)}
          onUpdate={s => setSettings(p => ({ ...p, ...s }))}
          onDeleteAccount={handleDeleteAccount}
          accent={accent}
        />
      )}
      <GlassSearchModal isOpen={showSearch} onClose={() => setShowSearch(false)} onSearch={handleSearch} isProcessing={isSearching} accent={accent} />
      <GlassPasteLinksModal isOpen={showPasteLinks} onClose={() => setShowPasteLinks(false)} onSubmit={async v => { setShowPasteLinks(false); await handleBulkImport(v); }} accent={accent} />
      {userId && <GlassSubscriptionModal isOpen={showSubscription} onClose={() => setShowSubscription(false)} userId={userId} session={authSession} onPlanChange={p => { setPlanData(p); setSubscriptionTier(p.tier); }} accent={accent} indexedItemsUsed={driveItemsUsed} usageLoading={usageLoading} />}
      <GlassUpgradePrompt isOpen={showUpgradePrompt} onClose={() => setShowUpgradePrompt(false)} reason={upgradeReason} currentTier={subscriptionTier} targetTier={targetUpgradeTier} userId={userId} accent={accent} />
      <GlassDropboxImportModal
        isOpen={showDropboxImportModal}
        initialPath={dropboxImportPath}
        initialRecursive={dropboxImportRecursive}
        loading={dropboxImportLoading}
        error={dropboxImportError}
        onCancel={() => {
          if (dropboxImportLoading) return;
          setShowDropboxImportModal(false);
          setDropboxImportError(null);
        }}
        onImport={async (path, recursive) => {
          setDropboxImportPath(path);
          setDropboxImportRecursive(recursive);
          await confirmDropboxImport(path, recursive);
        }}
      />
      <CollectionsModal
        isOpen={showCollections}
        collections={collections}
        selectedCollectionId={selectedCollectionId}
        currentItem={currentItem}
        enabled={collectionsEnabled}
        limit={collectionsLimit}
        onClose={() => setShowCollections(false)}
        onCreate={createCollection}
        onRename={renameCollection}
        onDelete={deleteCollection}
        onToggleCurrentItem={toggleCurrentItemCollection}
        onSelectCollection={setSelectedCollectionId}
        onUpgrade={handleUpgradeClick}
      />
      <SignOutConfirmModal
        isOpen={showSignOutModal}
        cloudBackupEnabled={cloudBackupEnabled}
        onCancel={() => setShowSignOutModal(false)}
        onSignOutOnly={async () => {
          setShowSignOutModal(false);
          await performLogout(false);
        }}
        onExportAndSignOut={async () => {
          setShowSignOutModal(false);
          await performLogout(true);
        }}
      />
      {showMobileMenu && (
        <div className="fixed inset-0 z-[85] md:hidden">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowMobileMenu(false)} />
          <div
            ref={mobileMenuRef}
            role="dialog"
            aria-modal="true"
            className="absolute right-3 top-14 bottom-3 w-[calc(100%-1.5rem)] max-w-sm glass-modal rounded-2xl p-3 space-y-1 overflow-y-auto overscroll-contain"
          >
            <button className="w-full text-left px-3 py-2 rounded-xl glass-panel text-white/70" onClick={() => { setShowMobileMenu(false); setShowSearch(true); }}>Search</button>
            <button className="w-full text-left px-3 py-2 rounded-xl glass-panel text-white/70" onClick={() => { setShowMobileMenu(false); fileInputRef.current?.click(); }}>Upload (+)</button>
            <button className="w-full text-left px-3 py-2 rounded-xl glass-panel text-white/70" onClick={() => { setShowMobileMenu(false); importFromPicker(); }}>Drive</button>
            <button className="w-full text-left px-3 py-2 rounded-xl glass-panel text-white/70" onClick={() => { setShowMobileMenu(false); importFromDropbox(); }}>Dropbox</button>
            <button className="w-full text-left px-3 py-2 rounded-xl glass-panel text-white/70" onClick={() => { setShowMobileMenu(false); setShowPasteLinks(true); }}>Link Import</button>
            <button className="w-full text-left px-3 py-2 rounded-xl glass-panel text-white/70" onClick={() => { setShowMobileMenu(false); setShowCollections(true); }}>Collections</button>
            <button className="w-full text-left px-3 py-2 rounded-xl glass-panel text-white/70" onClick={() => {
              setShowMobileMenu(false);
              if (isSearchMode) {
                const d = searchResults.filter(i => i.source !== 'local');
                const l = searchResults.filter(i => i.source === 'local');
                reshuffle(d, l, settings.filter);
              } else {
                reshuffle([...libraryItems, ...uploadThingItems], localItems, settings.filter);
              }
            }}>Shuffle</button>
            <button className="w-full text-left px-3 py-2 rounded-xl glass-panel text-white/70" onClick={() => { setShowMobileMenu(false); setShowSettings(true); }}>Settings</button>
            <button className="w-full text-left px-3 py-2 rounded-xl glass-panel text-white/70" onClick={async () => { setShowMobileMenu(false); await exportLocalManifest(); }}>Export manifest</button>
            <button className={`w-full text-left px-3 py-2 rounded-xl glass-panel text-blue-300/80 ${dropboxConnected ? '' : 'opacity-40 cursor-not-allowed'}`} disabled={!dropboxConnected} onClick={() => { setShowMobileMenu(false); syncDropboxChanges(); }}>Sync Dropbox</button>
            <button className="w-full text-left px-3 py-2 rounded-xl glass-panel text-white/70" onClick={() => { setShowMobileMenu(false); window.location.href = '/developers'; }}>Developer API</button>
            <button className="w-full text-left px-3 py-2 rounded-xl glass-panel text-white/70" onClick={() => { setShowMobileMenu(false); window.location.href = '/faq'; }}>FAQ</button>
            <button className="w-full text-left px-3 py-2 rounded-xl glass-panel text-yellow-300/80" onClick={() => { setShowMobileMenu(false); setShowSubscription(true); }}>Upgrade / Plans</button>
            <button className="w-full text-left px-3 py-2 rounded-xl glass-panel text-rose-300/80" onClick={() => { setShowMobileMenu(false); handleLogout(); }}>Sign out</button>
          </div>
        </div>
      )}

      <Toaster position="top-center" richColors />
    </div>
  );
};

export default PlayerDesign2;
